/**
 * API Service
 * Centralized API client for all backend calls
 */

import axios, { AxiosInstance, AxiosError } from 'axios';
import { APIResponse, Invoice, Customer, Product } from '@/types';

// En dev, Vite hace proxy de /api → localhost:3000 (ver vite.config.ts).
// En prod (Render), VITE_API_BASE apunta al servicio backend.
// Fallback a '/api' cuando la env no está definida (comportamiento local histórico).
const API_BASE_URL = import.meta.env.VITE_API_BASE
  ? `${String(import.meta.env.VITE_API_BASE).replace(/\/+$/, '')}/api/v1`
  : '/api/v1';

class APIClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add token to requests
    this.client.interceptors.request.use((config) => {
      const token = sessionStorage.getItem('token') || localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Handle response errors — normaliza CUALQUIER respuesta de error a un
    // mensaje legible en error.message + error.response.data.message para que
    // los alerts del UI nunca muestren "Unexpected token 'n', 'null' is not
    // valid JSON" ni "Request failed with status code 400" sin contexto.
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          window.location.href = '/login';
        }
        const r: any = error.response;
        const status = r?.status || 0;
        const friendly = (msg: string): string =>
          (msg || '').trim() || `Error HTTP ${status}` || 'Error desconocido';

        try {
          if (r?.data instanceof Blob) {
            const txt = await r.data.text().catch(() => '');
            let parsed: any = null;
            try { parsed = JSON.parse(txt); } catch { /* no era JSON */ }

            if (parsed && typeof parsed === 'object' && parsed.message) {
              r.data = parsed;
              (error as any).message = friendly(parsed.message);
            } else {
              const msg = friendly(txt);
              r.data = { message: msg };
              (error as any).message = msg;
            }
          } else if (r?.data && typeof r.data === 'object' && r.data.message) {
            (error as any).message = friendly(r.data.message);
          } else if (typeof r?.data === 'string') {
            const msg = friendly(r.data);
            r.data = { message: msg };
            (error as any).message = msg;
          } else if (status) {
            // No hay body usable — al menos ponemos el status
            (error as any).message = `Error HTTP ${status}`;
            if (r) r.data = { message: (error as any).message };
          }
        } catch (e) {
          // El interceptor JAMÁS debe lanzar un error que reemplace al original
          // con un mensaje de "Unexpected token..." al usuario.
          (error as any).message = friendly((error as any).message);
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * Auth endpoints
   */
  async login(email: string, password: string) {
    const response = await this.client.post<APIResponse<any>>('/auth/login', {
      email,
      password,
    });
    return response.data;
  }

  /* ── Alta pública de prueba (self-service, 72 h) ── */
  async registrarPrueba(payload: { rfc: string; razonSocial: string; cp: string; regimen: string; correo: string; nombre?: string; telefono?: string }) {
    const r = await this.client.post<APIResponse<any>>('/onboarding/registro', payload);
    return r.data; // { success, message, data: { correo, passwordTemporal } }
  }
  async getOnboardingEstado() {
    const r = await this.client.get<APIResponse<any>>('/onboarding/estado');
    return r.data; // { success, data: { estado, firmado, horasRestantes, ... } }
  }
  /** Vencimiento del sello (CSD) y la e.firma: { sellos, minDias, nivel }. */
  async getVigencias() {
    const r = await this.client.get<APIResponse<any>>('/vigencias');
    return r.data;
  }
  async solicitarDemo(payload: { contacto?: string; correo?: string; telefono?: string; mensaje?: string }) {
    const r = await this.client.post<APIResponse<any>>('/onboarding/demo', payload);
    return r.data;
  }
  /* Bandeja de demos (súper admin). */
  async getDemos(pendientes = false) {
    const r = await this.client.get<APIResponse<any>>('/onboarding/demos', { params: pendientes ? { pendientes: 'true' } : {} });
    return r.data;
  }
  async atenderDemoAdmin(id: string) {
    const r = await this.client.post<APIResponse<any>>(`/onboarding/demos/${id}/atender`, {});
    return r.data;
  }
  async getFirmas(pendientes = false) {
    const r = await this.client.get<APIResponse<any>>('/onboarding/firmas', { params: pendientes ? { pendientes: 'true' } : {} });
    return r.data;
  }
  async atenderFirma(id: string) {
    const r = await this.client.post<APIResponse<any>>(`/onboarding/firmas/${id}/atender`, {});
    return r.data;
  }

  /** Empresas a las que este usuario tiene acceso (multi-empresa). */
  async misEmpresas() {
    const r = await this.client.get<APIResponse<Array<{
      id: string; rfc: string; business_name: string;
      work_group: string | null; is_default: boolean;
      opinion_negativa?: number;
    }>>>('/auth/companies');
    return r.data;
  }

  /** Cambia la empresa activa. Devuelve un token NUEVO que hay que guardar. */
  async cambiarEmpresa(companyId: string) {
    const r = await this.client.post<APIResponse<{
      token: string;
      company: { id: string; rfc: string; business_name: string };
      workGroup: string;
    }>>('/auth/switch-company', { companyId });
    return r.data;
  }

  async logout() {
    return this.client.post('/auth/logout');
  }

  async changePassword(oldPassword: string, newPassword: string) {
    const r = await this.client.post('/auth/change-password', { oldPassword, newPassword });
    return r.data;
  }

  async refreshToken(refreshToken: string) {
    const response = await this.client.post<APIResponse<any>>('/auth/refresh', {
      refreshToken,
    });
    return response.data;
  }

  /**
   * Invoices endpoints
   */
  async getInvoices(page: number = 1, limit: number = 10, filters?: any) {
    const response = await this.client.get<APIResponse<any>>('/invoices', {
      params: { page, limit, ...filters },
    });
    return response.data;
  }

  async getInvoice(invoiceId: string) {
    const response = await this.client.get<APIResponse<Invoice>>(`/invoices/${invoiceId}`);
    return response.data;
  }

  async createInvoice(data: any) {
    const response = await this.client.post<APIResponse<Invoice>>('/invoices', data);
    return response.data;
  }

  async updateInvoice(invoiceId: string, data: any) {
    const response = await this.client.put<APIResponse<Invoice>>(`/invoices/${invoiceId}`, data);
    return response.data;
  }

  async deleteInvoice(invoiceId: string) {
    const response = await this.client.delete(`/invoices/${invoiceId}`);
    return response.data;
  }

  async changeInvoiceStatus(invoiceId: string, status: string) {
    const response = await this.client.put<APIResponse<Invoice>>(
      `/invoices/${invoiceId}/status`,
      { status }
    );
    return response.data;
  }

  /**
   * Customers endpoints
   */
  async getCustomers(page: number = 1, limit: number = 10, opts?: { sortBy?: string; sortOrder?: 'ASC' | 'DESC' }) {
    const response = await this.client.get<APIResponse<any>>('/customers', {
      params: { page, limit, sortBy: opts?.sortBy, sortOrder: opts?.sortOrder },
    });
    return response.data;
  }

  /**
   * Sube un PDF de Constancia de Situación Fiscal y devuelve campos extraídos.
   */
  async extractCSF(pdfFile: File) {
    const fd = new FormData();
    fd.append('pdf', pdfFile);
    const response = await this.client.post<APIResponse<any>>('/csf/extract', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async getCustomer(customerId: string) {
    const response = await this.client.get<APIResponse<Customer>>(`/customers/${customerId}`);
    return response.data;
  }

  async createCustomer(data: any) {
    const response = await this.client.post<APIResponse<Customer>>('/customers', data);
    return response.data;
  }

  async updateCustomer(customerId: string, data: any) {
    const response = await this.client.put<APIResponse<any>>(
      `/customers/${customerId}`,
      data
    );
    return response.data;
  }

  async deleteCustomer(customerId: string) {
    return this.client.delete(`/customers/${customerId}`);
  }

  async deleteProduct(productId: string) {
    return this.client.delete(`/products/${productId}`);
  }

  /**
   * Warehouses endpoints (módulo ALMACEN §7)
   */
  async getWarehouses(includeInactive = false) {
    const r = await this.client.get<APIResponse<any>>('/warehouses', {
      params: { includeInactive },
    });
    return r.data;
  }

  async createWarehouse(data: {
    code: string; name: string; address?: string;
    postalCode?: string; street?: string; extNumber?: string; intNumber?: string;
    colonia?: string; municipio?: string; estado?: string;
    /** Nombre legible del estado. Solo para armar el domicilio que se muestra;
     *  lo que se guarda como estado es la clave SAT. */
    estadoNombre?: string;
  }) {
    const r = await this.client.post<APIResponse<any>>('/warehouses', data);
    return r.data;
  }

  async updateWarehouse(
    id: string,
    data: {
      name?: string; address?: string; isActive?: boolean; isDefault?: boolean;
      postalCode?: string; street?: string; extNumber?: string; intNumber?: string;
      colonia?: string; municipio?: string; estado?: string; estadoNombre?: string;
    }
  ) {
    const r = await this.client.put<APIResponse<any>>(`/warehouses/${id}`, data);
    return r.data;
  }

  /* ─── Promoción: prueba de cortesía y cobro prepago prorrateado ─── */
  async promoEstadoPrueba() {
    const r = await this.client.get('/admin/promocion/prueba');
    return r.data;
  }
  async promoActivarPrueba(companyId: string) {
    const r = await this.client.post(`/admin/promocion/prueba/${companyId}`);
    return r.data;
  }
  /** Catálogo de paquetes con los precios que de verdad cobra el cierre. */
  async promoPaquetes() {
    const r = await this.client.get('/admin/promocion/paquetes');
    return r.data;
  }
  async promoCotizar(packageCode: string, desde?: string) {
    const q = new URLSearchParams({ packageCode });
    if (desde) q.set('desde', desde);
    const r = await this.client.get(`/admin/promocion/cotizar?${q}`);
    return r.data;
  }
  async promoCobros(status = 'PENDING') {
    const r = await this.client.get(`/admin/promocion/cobros?status=${status}`);
    return r.data;
  }
  async promoGenerarCobro(companyId: string, packageCode: string, desde?: string) {
    const r = await this.client.post('/admin/promocion/cobros', { companyId, packageCode, desde });
    return r.data;
  }
  async promoRegistrarPago(chargeId: string, nota?: string) {
    const r = await this.client.post(`/admin/promocion/cobros/${chargeId}/pagado`, { nota });
    return r.data;
  }
  async promoReavisar(chargeId: string) {
    const r = await this.client.post(`/admin/promocion/cobros/${chargeId}/avisar`);
    return r.data;
  }
  async promoFacturar(chargeId: string) {
    const r = await this.client.post(`/admin/promocion/cobros/${chargeId}/facturar`);
    return r.data;
  }

  async deleteWarehouse(id: string) {
    return this.client.delete(`/warehouses/${id}`);
  }

  /**
   * Inventory endpoints (módulo ALMACEN §1, §2, §7, §10)
   */
  async getInventoryStock(params?: {
    warehouseId?: string;
    search?: string;
    belowMin?: boolean;
    limit?: number;
    offset?: number;
  }) {
    const r = await this.client.get<APIResponse<any>>('/inventory/stock', { params });
    return r.data;
  }

  async getKardex(params?: {
    productId?: string;
    warehouseId?: string;
    type?: string;
    from?: string;
    to?: string;
    limit?: number;
    offset?: number;
  }) {
    const r = await this.client.get<APIResponse<any>>('/inventory/kardex', { params });
    return r.data;
  }

  async adjustInventory(data: {
    productId: string;
    warehouseId: string;
    direction?: 'IN' | 'OUT';
    movementType?: 'SHRINKAGE' | 'THEFT' | 'DAMAGED' | 'CUSTOMER_RETURN' | 'SUPPLIER_RETURN';
    quantity: number;
    unitCost?: number;
    reason: string;
  }) {
    const r = await this.client.post<APIResponse<any>>('/inventory/adjust', data);
    return r.data;
  }

  async transferInventory(data: {
    productId: string;
    warehouseFromId: string;
    warehouseToId: string;
    quantity: number;
    reason?: string;
  }) {
    const r = await this.client.post<APIResponse<any>>('/inventory/transfer', data);
    return r.data;
  }

  async setStockLimits(data: {
    productId: string;
    warehouseId: string;
    stockMinimum: number;
    stockMaximum: number;
  }) {
    const r = await this.client.put<APIResponse<any>>('/inventory/stock-limits', data);
    return r.data;
  }

  /**
   * Inventory reports endpoints (§12 + dashboard)
   */
  /** Kardex MENSUAL: la historia de un producto en un mes, con saldo corrido.
   *
   *  No confundir con `getKardex`, que ya existía y devuelve el listado crudo
   *  de movimientos filtrable por fechas. Éste responde a la pregunta del
   *  reporte —de cuánto partí, qué entró, qué salió, con qué cerré— y por eso
   *  lleva otro nombre en vez de sobrecargar el que ya estaba. */
  async getKardexMensual(p: { productId: string; anio: number; mes: number; warehouseId?: string }) {
    const q = new URLSearchParams({ productId: p.productId, anio: String(p.anio), mes: String(p.mes) });
    if (p.warehouseId) q.set('warehouseId', p.warehouseId);
    const r = await this.client.get<APIResponse<any>>(`/inventory/reports/kardex?${q}`);
    return r.data;
  }

  async getInventoryValue() {
    const r = await this.client.get<APIResponse<any>>('/inventory/reports/value');
    return r.data;
  }

  async getInventoryValueHistory(months = 12, warehouseId?: string) {
    const r = await this.client.get<APIResponse<any>>('/inventory/reports/value-history', {
      params: { months, warehouseId },
    });
    return r.data;
  }

  async takeInventorySnapshot() {
    const r = await this.client.post<APIResponse<any>>('/inventory/reports/snapshot');
    return r.data;
  }

  async getInventoryRotation(order: 'rotation' | 'no-movement' = 'rotation', limit = 100) {
    const r = await this.client.get<APIResponse<any>>('/inventory/reports/rotation', {
      params: { order, limit },
    });
    return r.data;
  }

  async getInventoryCountDue(all = false) {
    const r = await this.client.get<APIResponse<any>>('/inventory/reports/count-due', {
      params: { all },
    });
    return r.data;
  }

  /** Catálogo de reportes exportables (§12). */
  async getReportCatalog() {
    const r = await this.client.get<APIResponse<any>>('/inventory/reports/catalog');
    return r.data;
  }

  /**
   * Descarga un reporte de inventario en Excel o PDF. Usa axios (blob) para
   * que viaje el header de autenticación, y dispara la descarga en el navegador.
   */
  async downloadInventoryReport(
    report: string,
    format: 'xlsx' | 'pdf',
    params?: { from?: string; to?: string; warehouseId?: string }
  ) {
    const r = await this.client.get('/inventory/reports/export', {
      params: { report, format, ...params },
      responseType: 'blob',
    });
    const blob = new Blob([r.data], {
      type: format === 'pdf'
        ? 'application/pdf'
        : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${report}-${new Date().toISOString().slice(0, 10)}.${format}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  }

  /**
   * Purchase orders endpoints (§3 ALMACEN — Fase 4)
   */
  async getPurchaseOrders(params?: {
    status?: string; warehouseId?: string; limit?: number;
    /** Sin esto la lista trae sólo las órdenes vivas (ni surtidas ni canceladas). */
    includeClosed?: boolean;
  }) {
    const r = await this.client.get<APIResponse<any>>('/purchase-orders', { params });
    return r.data;
  }

  async getPurchaseOrder(id: string) {
    const r = await this.client.get<APIResponse<any>>(`/purchase-orders/${id}`);
    return r.data;
  }

  async runReorderCheck() {
    const r = await this.client.post<APIResponse<any>>('/purchase-orders/reorder-check');
    return r.data;
  }

  /** Faltantes: qué comprar, SIN generar nada todavía. */
  async getFaltantes() {
    const r = await this.client.get<APIResponse<any>>('/purchase-orders/faltantes');
    return r.data;
  }

  async createPurchaseOrder(data: {
    warehouseId: string;
    supplierId?: string;
    neededByDate?: string;
    notes?: string;
    items: Array<{ productId: string; quantity: number }>;
  }) {
    const r = await this.client.post<APIResponse<any>>('/purchase-orders', data);
    return r.data;
  }

  async setPurchaseOrderStatus(id: string, status: string) {
    const r = await this.client.put<APIResponse<any>>(`/purchase-orders/${id}/status`, { status });
    return r.data;
  }

  /**
   * Registra la factura que llegó DESPUÉS de haber recibido la mercancía.
   * Genera la deuda en tesorería; no vuelve a mover existencias.
   */
  /** Da de alta un proveedor con lo mínimo: nombre y días de crédito. */
  async preregistrarProveedor(nombre: string, creditDays?: number) {
    const r = await this.client.post<APIResponse<any>>(
      '/purchase-orders/proveedor-preregistro', { nombre, creditDays });
    return r.data;
  }

  /** Los proveedores capturados al vuelo que siguen sin RFC. */
  async getProveedoresAMedias() {
    const r = await this.client.get<APIResponse<any>>('/purchase-orders/proveedores-a-medias');
    return r.data;
  }

  async registrarFacturaDeOrden(id: string, datos: {
    invoiceNumber: string; subtotal?: number; taxRate?: number;
    invoiceDate?: string; supplierId?: string;
  }) {
    const r = await this.client.post<APIResponse<any>>(
      `/purchase-orders/${id}/invoice`, datos
    );
    return r.data;
  }

  /** Cambia el proveedor al que se le compra — el sugerido es sólo una propuesta. */
  async setPurchaseOrderSupplier(id: string, supplierId: string | null) {
    const r = await this.client.put<APIResponse<any>>(
      `/purchase-orders/${id}/supplier`, { supplierId }
    );
    return r.data;
  }

  async receivePurchaseOrder(
    id: string,
    receipts: Array<{ itemId: string; quantity: number; unitCost?: number }>,
    costingMethod?: 'PROMEDIO' | 'ULTIMO' | 'CAPAS',
    /** Factura del proveedor: con ella la recepción genera la deuda en tesorería. */
    factura?: {
      invoiceNumber?: string; subtotal?: number; taxRate?: number; invoiceDate?: string;
    }
  ) {
    const r = await this.client.post<APIResponse<any>>(`/purchase-orders/${id}/receive`, {
      receipts,
      costingMethod,
      ...factura,
    });
    return r.data;
  }

  /**
   * POS endpoints (Fase 5 ALMACEN)
   */
  async createPosSale(data: {
    warehouseId?: string;
    paymentForm?: string;
    items: Array<{ productId: string; quantity: number; unitPrice?: number }>;
  }) {
    const r = await this.client.post<APIResponse<any>>('/pos/sales', data);
    return r.data;
  }

  async getPosSales(date?: string) {
    const r = await this.client.get<APIResponse<any>>('/pos/sales', { params: { date } });
    return r.data;
  }
  /** Lector del POS: un producto por código de barras (o SKU) EXACTO. */
  async scanPos(code: string) {
    const r = await this.client.get<APIResponse<any>>('/pos/scan', { params: { code } });
    return r.data;
  }

  /** El cliente pidió su factura de una venta de mostrador. */
  async facturarVentaPos(saleId: string, customerId: string, cfdiUse?: string) {
    const r = await this.client.post(`/pos/sales/${saleId}/facturar`, { customerId, cfdiUse });
    return r.data;
  }

  async cancelPosSale(id: string) {
    const r = await this.client.post<APIResponse<any>>(`/pos/sales/${id}/cancel`);
    return r.data;
  }

  async closePosDay(date?: string) {
    const r = await this.client.post<APIResponse<any>>('/pos/close-day', { date });
    return r.data;
  }

  /**
   * Envía por correo los archivos seleccionados (PDF y XML de factura, NCs y pagos).
   * El backend usa el `contact_email` de la empresa emisora como remitente si está
   * configurado; si no, cae al MAIL_FROM del env.
   */
  async sendInvoiceMail(invoiceId: string, body: {
    to: string;
    cc?: string;
    subject: string;
    message: string;
    attachments: Array<{ kind: string; id: string }>;
  }) {
    const r = await this.client.post(`/invoices/${invoiceId}/send-email`, body);
    return r.data;
  }

  /**
   * Products endpoints
   */
  async getProducts(page: number = 1, limit: number = 10, search?: string) {
    const response = await this.client.get<APIResponse<any>>('/products', {
      params: { page, limit, search },
    });
    return response.data;
  }

  async getProduct(productId: string) {
    const response = await this.client.get<APIResponse<Product>>(`/products/${productId}`);
    return response.data;
  }

  async createProduct(data: any) {
    const response = await this.client.post<APIResponse<Product>>('/products', data);
    return response.data;
  }

  async updateProduct(productId: string, data: any) {
    const response = await this.client.put<APIResponse<Product>>(`/products/${productId}`, data);
    return response.data;
  }

  /**
   * CFDI endpoints
   */
  async generateCFDI(invoiceId: string) {
    const response = await this.client.post(`/cfdi/${invoiceId}/generate`);
    return response.data;
  }

  async getCFDIStatus(invoiceId: string) {
    const response = await this.client.get(`/cfdi/${invoiceId}/status`);
    return response.data;
  }

  async getCFDIXML(invoiceId: string) {
    const response = await this.client.get(`/cfdi/${invoiceId}/xml`, {
      responseType: 'blob',
    });
    return response.data;
  }

  async generatePDF(invoiceId: string) {
    // GET evita el body 'null' que axios serializa en POSTs sin data y que
    // express.json() rechaza con "Unexpected token 'n', 'null' is not valid JSON".
    const response = await this.client.get(`/cfdi/${invoiceId}/pdf`, {
      responseType: 'blob',
    });
    return response.data;
  }

  async previewPDF(invoiceId: string) {
    const response = await this.client.get(`/cfdi/${invoiceId}/pdf/preview`, {
      responseType: 'blob',
    });
    return response.data;
  }

  /**
   * SAT Validator endpoints
   */
  async validateWithSAT(invoiceId: string) {
    const response = await this.client.post(`/sat-validator/validate/${invoiceId}`);
    return response.data;
  }

  async getSATValidationStatus(invoiceId: string) {
    const response = await this.client.get(`/sat-validator/status/${invoiceId}`);
    return response.data;
  }

  async getSATValidationStats() {
    const response = await this.client.get('/sat-validator/stats');
    return response.data;
  }

  /**
   * Reports endpoints
   */
  async getCollectionsReport() {
    const response = await this.client.get('/reports/collections');
    return response.data;
  }

  /** Reporte de cobranza detallado — facturas por cliente con saldo > 0.20. */
  async getReceivablesReport(customerId?: string) {
    const response = await this.client.get('/reports/receivables', {
      params: customerId ? { customerId } : {},
    });
    return response.data;
  }

  /** PDF del reporte de cobranza (misma URL, filtro opcional por cliente). */
  receivablesPDFUrl(customerId?: string): string {
    const base = `${this.client.defaults.baseURL}/reports/receivables/pdf`;
    return customerId ? `${base}?customerId=${customerId}` : base;
  }

  async getSalesReport(dateFrom?: string, dateTo?: string) {
    const response = await this.client.get('/reports/sales', {
      params: { dateFrom, dateTo },
    });
    return response.data;
  }

  async getTaxReport(dateFrom?: string, dateTo?: string) {
    const response = await this.client.get('/reports/tax', {
      params: { dateFrom, dateTo },
    });
    return response.data;
  }

  async getStatusReport() {
    const response = await this.client.get('/reports/status');
    return response.data;
  }

  async getDashboardMetrics() {
    const response = await this.client.get('/reports/dashboard');
    return response.data;
  }

  /**
   * PAC endpoints (timbrado - MODO MOCK)
   */
  async stampInvoice(invoiceId: string) {
    const response = await this.client.post(`/pac/stamp/${invoiceId}`);
    return response.data;
  }

  /** Qué dice el SAT de esta factura: vigente, cancelable, cancelación en curso. */
  async estatusSat(invoiceId: string) {
    const r = await this.client.get<APIResponse<{
      encontrado: boolean; estado: string; esCancelable: string;
      estatusCancelacion: string; codigoEstatus: string;
      validacionEfos: string; resumen: string; error?: string;
    }>>(`/pac/estatus-sat/${invoiceId}`);
    return r.data;
  }

  async cancelInvoice(
    invoiceId: string,
    motivo: string,
    folioSustitucion?: string,
    forceLocal?: boolean
  ) {
    const response = await this.client.post(`/pac/cancel/${invoiceId}`, {
      motivo,
      folioSustitucion,
      forceLocal,
    });
    return response.data;
  }

  /** Cancela el complemento ante el SAT. motivoSat es la clave del Anexo 20. */
  async cancelPayment(paymentId: string, motivo?: string, motivoSat?: string, folioSustitucion?: string, soloLocal?: boolean) {
    const r = await this.client.post<APIResponse<any>>(`/payments/${paymentId}/cancel`, {
      motivo, motivoSat, folioSustitucion, soloLocal,
    });
    return r.data;
  }

  /** Cancela la NC ante el SAT. motivoSat es la clave del Anexo 20. */
  async cancelCreditNote(creditNoteId: string, motivo?: string, motivoSat?: string, folioSustitucion?: string, soloLocal?: boolean) {
    const r = await this.client.post<APIResponse<any>>(`/credit-notes/${creditNoteId}/cancel`, {
      motivo, motivoSat, folioSustitucion, soloLocal,
    });
    return r.data;
  }

  async getPACAccountStatus() {
    const response = await this.client.get('/pac/account-status');
    return response.data;
  }

  async getPACProviders() {
    const response = await this.client.get('/pac/providers');
    return response.data;
  }

  /**
   * Catálogos SAT (regimenFiscal | usoCfdi | estado | formaPago | ...)
   */
  async getCatalog(name: string) {
    const response = await this.client.get(`/catalogs/${name}`);
    return response.data;
  }

  /** Próximo SKU automático "P-N" */
  async getNextProductSku() {
    const r = await this.client.get(`/products/next-sku`);
    return r.data;
  }

  /** Búsqueda SAT para autocompletar combos (type=prodserv|unidad) */
  async searchSAT(type: 'prodserv' | 'unidad', q: string, limit = 20) {
    const r = await this.client.get(`/products/sat-search`, { params: { type, q, limit } });
    return r.data;
  }

  /** Sube uno o varios XMLs de CFDI para alimentar el catálogo de productos */
  async importProductsFromXML(files: File[]) {
    const fd = new FormData();
    files.forEach((f) => fd.append('files', f));
    const r = await this.client.post(`/products/import-xml`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return r.data;
  }

  /** Descarga los productos actuales en una plantilla Excel (editable y re-cargable). */
  async descargarPlantillaProductos() {
    const r = await this.client.get('/products/plantilla.xlsx', { responseType: 'blob' });
    await this.downloadFile(r.data as Blob, 'Plantilla_productos.xlsx');
  }
  /** Carga productos en lote desde la plantilla Excel. Devuelve { creados, actualizados, errores }. */
  async importarPlantillaProductos(file: File) {
    const fd = new FormData();
    fd.append('file', file);
    const r = await this.client.post('/products/importar-plantilla', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    return r.data;
  }

  /** Memoria cliente↔productos (qué productos compra cada cliente) */
  async getCustomerProducts(customerId: string) {
    const r = await this.client.get(`/customers/${customerId}/products`);
    return r.data;
  }

  /**
   * Empresa emisora — leer y actualizar datos del emisor
   */
  async getCompany(id: string) {
    const response = await this.client.get(`/companies/${id}`);
    return response.data;
  }

  async updateCompanySMTP(id: string, data: { mail_host: string; mail_port: number; mail_secure: boolean; mail_user: string; mail_pass?: string; mail_from?: string }) {
    const r = await this.client.patch<any>(`/companies/${id}/smtp`, data);
    return r.data;
  }
  async testCompanySMTP(id: string) {
    const r = await this.client.post<any>(`/companies/${id}/smtp/test`, {});
    return r.data;
  }
  async updateCompany(id: string, data: any) {
    const response = await this.client.put(`/companies/${id}`, data);
    return response.data;
  }

  /** Sube logo de la empresa (multipart) */
  async uploadCompanyLogo(id: string, file: File) {
    const fd = new FormData();
    fd.append('logo', file);
    const r = await this.client.post(`/companies/${id}/logo`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return r.data;
  }

  /** Sube CSD (.cer + .key + contraseña) para timbrar */
  async uploadCompanyCSD(id: string, cer: File, key: File, password: string) {
    const fd = new FormData();
    fd.append('cer', cer);
    fd.append('key', key);
    fd.append('password', password);
    const r = await this.client.post(`/companies/${id}/csd`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return r.data;
  }

  /** Estatus de subidas (qué tiene el emisor cargado) */
  async getCompanyUploadStatus(id: string) {
    const r = await this.client.get(`/companies/${id}/csd-status`);
    return r.data;
  }

  /**
   * URL pública del logo — para usar directo en `<img src>`.
   * Usa el mismo baseURL configurado en el cliente axios, así funciona:
   *   · Dev: proxy Vite → localhost:3000
   *   · Prod: apunta al backend en Render (VITE_API_BASE)
   */
  companyLogoUrl(id: string) {
    const base = this.client.defaults.baseURL || '/api/v1';
    return `${base}/public/companies/${id}/logo?t=${Date.now()}`;
  }

  /* ───────────── Complemento de Pago ───────────── */

  async createPayment(data: {
    /* Forma antigua, una factura. Se conserva para las llamadas que ya existen. */
    invoiceId?: string;
    paymentAmount?: number;
    /* Forma nueva: varias facturas del mismo cliente en un solo comprobante. */
    documentos?: Array<{ invoiceId: string; monto: number }>;
    paymentDate?: string;
    paymentForm: string;
    paymentMethod?: string;
    currency?: string;
    notes?: string;
  }) {
    const r = await this.client.post('/payments', data);
    return r.data;
  }

  /**
   * La remesa en una hoja. `inline` la abre en el navegador para imprimirla;
   * sin él, se descarga. Los dos usan el MISMO PDF: si fueran dos, tarde o
   * temprano dirían cosas distintas.
   */
  async abrirRemesaPdf(runId: string, inline = true) {
    const r = await this.client.get(
      `/treasury/payment-runs/${runId}/pdf`,
      { params: { inline: inline ? 1 : 0 }, responseType: 'blob' }
    );
    const url = URL.createObjectURL(new Blob([r.data as Blob], { type: 'application/pdf' }));
    if (inline) {
      const w = window.open(url, '_blank');
      /* Si el navegador bloquea la ventana, se descarga: peor es no dar nada
       * y que parezca que el botón no hace nada. */
      if (!w) await this.downloadFile(new Blob([r.data as Blob]), `remesa-${runId.slice(0, 8)}.pdf`);
      else setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } else {
      await this.downloadFile(new Blob([r.data as Blob]), `remesa-${runId.slice(0, 8)}.pdf`);
    }
  }

  /** Qué puede HACER el usuario actual: rol + grupo + otorgamientos. */
  async getMisCapacidades() {
    const r = await this.client.get<APIResponse<any>>('/auth/mis-capacidades');
    return r.data;
  }

  /* ── Bancos ── */
  async getCuentasBancarias() {
    const r = await this.client.get<APIResponse<any>>('/treasury/bancos/cuentas');
    return r.data;
  }
  async crearCuentaBancaria(datos: any) {
    const r = await this.client.post<APIResponse<any>>('/treasury/bancos/cuentas', datos);
    return r.data;
  }
  async borrarCuentaBancaria(id: string) {
    const r = await this.client.delete<APIResponse<any>>(`/treasury/bancos/cuentas/${id}`);
    return r.data;
  }
  /**
   * Carga un estado de cuenta: archivo o texto pegado.
   *
   * Va como multipart aunque sólo se mande texto, para tener UN camino: dos
   * rutas para lo mismo garantizan que una se quede atrás.
   */
  async cargarEstadoDeCuenta(d: {
    cuentaId: string; anio: number; mes: number; texto?: string; archivo?: File;
  }) {
    const fd = new FormData();
    fd.append('cuentaId', d.cuentaId);
    fd.append('anio', String(d.anio));
    fd.append('mes', String(d.mes));
    if (d.texto) fd.append('texto', d.texto);
    if (d.archivo) fd.append('archivo', d.archivo);
    const r = await this.client.post<APIResponse<any>>('/treasury/bancos/estados', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return r.data;
  }
  /** Plantilla de Excel para capturar un estado de cuenta (cuando sólo hay resumen). */
  async descargarPlantillaEstado() {
    const r = await this.client.get('/treasury/bancos/estados/plantilla', { responseType: 'blob' });
    await this.downloadFile(r.data as Blob, 'Plantilla_estado_de_cuenta.xlsx');
  }
  async actualizarCuentaBancaria(id: string, datos: any) {
    const r = await this.client.put<APIResponse<any>>(`/treasury/bancos/cuentas/${id}`, datos);
    return r.data;
  }
  async getEstadosBancarios(cuentaId: string) {
    const r = await this.client.get<APIResponse<any>>(`/treasury/bancos/cuentas/${cuentaId}/estados`);
    return r.data;
  }
  /* ── Conciliación banco → contabilidad ── */
  async getBancosConfig() {
    const r = await this.client.get<APIResponse<any>>('/treasury/bancos/config');
    return r.data;
  }
  async setCuentasComisiones(comisionesId: string | null, ivaId: string | null, interesesId?: string | null) {
    const r = await this.client.put<APIResponse<any>>('/treasury/bancos/config/comisiones', { comisionesId, ivaId, interesesId });
    return r.data;
  }
  async getConciliacion(estadoId: string) {
    const r = await this.client.get<APIResponse<any>>(`/treasury/bancos/estados/${estadoId}/conciliacion`);
    return r.data;
  }
  async sugerirConciliacion(estadoId: string) {
    const r = await this.client.post<APIResponse<any>>(`/treasury/bancos/estados/${estadoId}/sugerir`, {});
    return r.data;
  }
  async contabilizarEstado(estadoId: string) {
    const r = await this.client.post<APIResponse<any>>(`/treasury/bancos/estados/${estadoId}/contabilizar`, {});
    return r.data;
  }
  /** Cateja los movimientos del banco contra la contabilidad ya asentada (102). */
  async cotejarConLibro(estadoId: string) {
    const r = await this.client.post<APIResponse<any>>(`/treasury/bancos/estados/${estadoId}/cotejar`, {});
    return r.data;
  }
  /** Movimientos de la cuenta 102 del banco en el periodo (el "libro"). */
  async getLibroBanco(estadoId: string) {
    const r = await this.client.get<APIResponse<any>>(`/treasury/bancos/estados/${estadoId}/libro`);
    return r.data;
  }
  /** El documento formal de conciliación bancaria (PDF imprimible). */
  async descargarConciliacionBancaria(estadoId: string) {
    const r = await this.client.get(`/treasury/bancos/estados/${estadoId}/conciliacion/pdf`, { responseType: 'blob' });
    await this.downloadFile(r.data as Blob, this.nombreDeHeader(r, `Conciliacion_${estadoId.slice(0, 8)}.pdf`));
  }
  /** PDF con la cobertura de bancos: los que procesamos y los pendientes. */
  async descargarBancosSoportados() {
    const r = await this.client.get('/treasury/bancos/soportados/pdf', { responseType: 'blob' });
    await this.downloadFile(r.data as Blob, 'Cobertura_bancos.pdf');
  }
  async marcarMovimiento(id: string, data: any) {
    const r = await this.client.put<APIResponse<any>>(`/treasury/bancos/movimientos/${id}/marcar`, data);
    return r.data;
  }
  async contabilizarMovimiento(id: string, contraCuentaId?: string) {
    const r = await this.client.post<APIResponse<any>>(`/treasury/bancos/movimientos/${id}/contabilizar`, { contraCuentaId });
    return r.data;
  }
  /** Recibos de nómina timbrados por pagar (modo "Nómina" de la conciliación). */
  async getNominaPorPagar() {
    const r = await this.client.get<APIResponse<any[]>>('/treasury/bancos/nomina-por-pagar');
    return r.data;
  }
  /** Contabiliza un retiro como pago de nómina contra los recibos elegidos. */
  async contabilizarNominaMovimiento(id: string, reciboIds: string[]) {
    const r = await this.client.post<APIResponse<any>>(`/treasury/bancos/movimientos/${id}/contabilizar-nomina`, { reciboIds });
    return r.data;
  }
  /** Facturas recibidas (compras) por pagar — modo "Proveedor" de la conciliación. */
  async getFacturasRecibidasPorPagar() {
    const r = await this.client.get<APIResponse<any[]>>('/treasury/bancos/facturas-por-pagar');
    return r.data;
  }
  /** Facturas emitidas (ventas) por cobrar — modo "Cliente" de un depósito. */
  async getFacturasEmitidasPorCobrar() {
    const r = await this.client.get<APIResponse<any[]>>('/treasury/bancos/facturas-por-cobrar');
    return r.data;
  }
  /** Contabilización especial: 'ingreso' (depósito con IVA) o 'proveedor_pasivo' (retiro PPD). */
  async contabilizarEspecialMovimiento(id: string, payload: any) {
    const r = await this.client.post<APIResponse<any>>(`/treasury/bancos/movimientos/${id}/contabilizar-especial`, payload);
    return r.data;
  }
  async descontabilizarMovimiento(id: string) {
    const r = await this.client.post<APIResponse<any>>(`/treasury/bancos/movimientos/${id}/descontabilizar`, {});
    return r.data;
  }
  /** Cotejo manual: liga uno o varios movimientos del banco con una línea de la 102. */
  async cotejarManualMovimientos(movIds: string[], lineId: string) {
    const r = await this.client.post<APIResponse<any>>('/treasury/bancos/cotejar-manual', { movIds, lineId });
    return r.data;
  }
  /* ═══════════ CONTABILIDAD ═══════════ */

  async getCuentasContables(params?: {
    q?: string; tipo?: string; soloMovimientos?: boolean; nivel?: number;
  }) {
    const r = await this.client.get<APIResponse<any>>('/accounting/cuentas', { params });
    return r.data;
  }
  /** ¿La empresa ya tiene catálogo contable? Decide qué se ofrece en el menú. */
  async getEstadoContabilidad() {
    const r = await this.client.get<APIResponse<{ cuentas: number; polizas: number; activa: boolean }>>('/accounting/estado');
    return r.data;
  }
  /** Cuentas fijas asignadas para Cobros y pagos (IVA, banco): { agrupador: {account_id, codigo, nombre} }. */
  async getCuentasFijas() {
    const r = await this.client.get<APIResponse<Record<string, any>>>('/accounting/cuentas-fijas');
    return r.data;
  }
  async setCuentaFija(agrupador: string, account_id: string | null) {
    const r = await this.client.put<APIResponse<any>>('/accounting/cuentas-fijas', { agrupador, account_id });
    return r.data;
  }
  /** Cuentas por cobrar por cliente, desde la contabilidad (subcuentas 105-xx). */
  async getSaldosClientesContable() {
    const r = await this.client.get<APIResponse<{ clientes: any[]; total: number; cuantos: number }>>('/invoices/saldos-clientes-contable');
    return r.data;
  }
  /** Cierre del ejercicio: utilidad/pérdida y cuentas de resultados del año. */
  async getCierreEjercicio(anio: number) {
    const r = await this.client.get<APIResponse<any>>(`/accounting/cierre/${anio}`);
    return r.data;
  }
  /** Genera/regenera la póliza de cierre (ADMIN). cuentaResultadoId: cuenta de
   *  capital elegida para acumular el resultado (opcional; si no, automático). */
  async generarCierreEjercicio(anio: number, cuentaResultadoId?: string) {
    const r = await this.client.post<APIResponse<any>>(`/accounting/cierre/${anio}/generar`, { cuentaResultadoId });
    return r.data;
  }
  /** Deshace la póliza de cierre del año (ADMIN). */
  async revertirCierreEjercicio(anio: number) {
    const r = await this.client.delete<APIResponse<any>>(`/accounting/cierre/${anio}`);
    return r.data;
  }
  /* ── Cierre MENSUAL ── */
  async getCierreMes(anio: number, mes: number) {
    const r = await this.client.get<APIResponse<any>>(`/accounting/cierre/${anio}/${mes}`);
    return r.data;
  }
  /** Meses del año que ya tienen cierre (para reabrir y corregir). */
  async getMesesCerrados(anio: number) {
    const r = await this.client.get<APIResponse<number[]>>(`/accounting/meses-cerrados/${anio}`);
    return r.data;
  }
  async generarCierreMes(anio: number, mes: number, cuentaResultadoId?: string) {
    const r = await this.client.post<APIResponse<any>>(`/accounting/cierre/${anio}/${mes}/generar`, { cuentaResultadoId });
    return r.data;
  }
  async revertirCierreMes(anio: number, mes: number) {
    const r = await this.client.delete<APIResponse<any>>(`/accounting/cierre/${anio}/${mes}`);
    return r.data;
  }
  /** Máscara de despliegue del código de cuenta (por empresa). */
  async getMascaraCuenta(): Promise<string> {
    const r = await this.client.get<APIResponse<any>>('/accounting/mascara');
    return r.data?.data?.mascara || '';
  }
  async setMascaraCuenta(mascara: string): Promise<string> {
    const r = await this.client.put<APIResponse<any>>('/accounting/mascara', { mascara });
    return r.data?.data?.mascara || '';
  }
  /** Años (ejercicios) de la empresa, del más nuevo al más viejo, para el combo de año. */
  async getEjerciciosContables(): Promise<number[]> {
    const r = await this.client.get<APIResponse<any>>('/accounting/ejercicios');
    return r.data?.data?.anios || [];
  }
  /** Cambio de cuenta: reasignar partidas (sustituir temporal), fusionar duplicadas. */
  async reasignarCuenta(origenId: string, destinoId: string, desde?: string, hasta?: string) {
    const r = await this.client.post<APIResponse<any>>('/accounting/cuentas/reasignar',
      { origenId, destinoId, desde, hasta });
    return r.data;
  }
  async fusionarCuenta(origenId: string, destinoId: string) {
    const r = await this.client.post<APIResponse<any>>('/accounting/cuentas/fusionar', { origenId, destinoId });
    return r.data;
  }
  async cuentasDuplicadas(q?: string) {
    const r = await this.client.get<APIResponse<any>>('/accounting/cuentas/duplicadas', { params: { q } });
    return r.data;
  }
  async getArbolDeCuentas() {
    const r = await this.client.get<APIResponse<any>>('/accounting/cuentas/arbol');
    return r.data;
  }
  async getAgrupadoresSat() {
    const r = await this.client.get<APIResponse<any>>('/accounting/agrupadores');
    return r.data;
  }
  async getRevisionCatalogo() {
    const r = await this.client.get<APIResponse<any>>('/accounting/cuentas/revision');
    return r.data;
  }
  async getCuentaContable(id: string) {
    const r = await this.client.get<APIResponse<any>>(`/accounting/cuentas/${id}`);
    return r.data;
  }
  /** Mayor auxiliar de una cuenta en un rango de fechas (con saldo corriente). */
  async getAuxiliarRango(id: string, desde: string, hasta: string) {
    const r = await this.client.get<APIResponse<any>>(`/accounting/cuentas/${id}/auxiliar`, { params: { desde, hasta } });
    return r.data;
  }
  async crearCuentaContable(datos: any) {
    const r = await this.client.post<APIResponse<any>>('/accounting/cuentas', datos);
    return r.data;
  }
  async actualizarCuentaContable(id: string, datos: any) {
    const r = await this.client.patch<APIResponse<any>>(`/accounting/cuentas/${id}`, datos);
    return r.data;
  }
  async desactivarCuentaContable(id: string) {
    const r = await this.client.delete<APIResponse<any>>(`/accounting/cuentas/${id}`);
    return r.data;
  }
  /** Borrado REAL (para limpiar el catálogo importado). Falla si tiene subcuentas o movimientos. */
  async eliminarCuentaContable(id: string) {
    const r = await this.client.delete<APIResponse<any>>(`/accounting/cuentas/${id}`, { params: { duro: 1 } });
    return r.data;
  }
  /** Las partidas (renglones de póliza) que tocan una cuenta — para Cambio de cuenta. */
  async getPartidasCuenta(id: string, desde?: string, hasta?: string) {
    const r = await this.client.get<APIResponse<any>>(`/accounting/cuentas/${id}/partidas`, { params: { desde, hasta } });
    return r.data;
  }
  /** Base para sugerir cuenta por clave SAT (claves asignadas + dominante). */
  async getSugerenciasCuenta(direccion: 'ventas' | 'compras') {
    const r = await this.client.get<APIResponse<any>>('/accounting/sugerencias-cuenta', { params: { direccion } });
    return r.data;
  }
  /** Auto-asigna «de un tirón» las cuentas de producto con match claro (una dirección). */
  async autoAsignarCuentas(direccion: 'ventas' | 'compras', anio: number, mes: number) {
    const r = await this.client.post<APIResponse<any>>('/accounting/cuentas/auto-asignar', { direccion, anio, mes });
    return r.data;
  }
  /** «De un tirón» completo: ventas + compras + subcuentas de terceros del periodo. */
  async autoAsignarCuentasTodo(anio: number, mes: number) {
    const r = await this.client.post<APIResponse<any>>('/accounting/cuentas/auto-asignar-todo', { anio, mes });
    return r.data;
  }
  /** Diagnóstico de contabilización de CFDI del periodo (cuadre / organización de XML). */
  async getDiagnosticoCuadre(anio: number, mes: number) {
    const r = await this.client.get<APIResponse<any>>(`/accounting/cuadre-cfdi/${anio}/${mes}`);
    return r.data;
  }

  /* ── Cédulas fiscales (papel de trabajo): ISR PF 612 + IVA ── */
  async getCedulaRegimen() {
    const r = await this.client.get<APIResponse<any>>('/accounting/cedulas/regimen');
    return r.data;
  }
  async getCedulaIsrPF(anio: number) {
    const r = await this.client.get<APIResponse<any>>(`/accounting/cedulas/isr-pf/${anio}`);
    return r.data;
  }
  async getCedulaIva(anio: number) {
    const r = await this.client.get<APIResponse<any>>(`/accounting/cedulas/iva/${anio}`);
    return r.data;
  }
  /** Cédula de IVA a Excel (resalta IVA a cargo y la columna del mes vigente). */
  async getCedulaIvaExcel(anio: number) {
    const r = await this.client.get(`/accounting/cedulas/iva/${anio}/excel`, { responseType: 'blob' });
    await this.downloadFile(r.data as Blob, `Cedula_IVA_${anio}.xlsx`);
  }
  async getCedulaResico(anio: number) {
    const r = await this.client.get<APIResponse<any>>(`/accounting/cedulas/resico/${anio}`);
    return r.data;
  }
  async getCedulaPM601(anio: number) {
    const r = await this.client.get<APIResponse<any>>(`/accounting/cedulas/pm-601/${anio}`);
    return r.data;
  }
  async setCoeficienteUtilidad(anio: number, coeficiente: number) {
    const r = await this.client.put<APIResponse<any>>(`/accounting/cedulas/coeficiente/${anio}`, { coeficiente });
    return r.data;
  }
  async getCedulaPlataformas(anio: number) {
    const r = await this.client.get<APIResponse<any>>(`/accounting/cedulas/plataformas/${anio}`);
    return r.data;
  }
  async setCedulaPlataformas(anio: number, filas: Array<{ mes: number; actividad: string; ingreso: number }>) {
    const r = await this.client.put<APIResponse<any>>(`/accounting/cedulas/plataformas/${anio}`, { filas });
    return r.data;
  }

  /* ── Opinión de Cumplimiento (SAT 32-D / IMSS / INFONAVIT) ── */
  async getOpinionCumplimiento() {
    const r = await this.client.get<APIResponse<any>>('/accounting/opinion-cumplimiento');
    return r.data;
  }
  async getOpinionHistorial(tipo?: string) {
    const r = await this.client.get<APIResponse<any>>('/accounting/opinion-cumplimiento/historial', { params: { tipo } });
    return r.data;
  }
  async registrarOpinion(data: any) {
    const r = await this.client.post<APIResponse<any>>('/accounting/opinion-cumplimiento', data);
    return r.data;
  }
  async leerOpinionPdf(pdf: string) {
    // NEXO lee el PDF (pdf-parse) y detecta sentido/fecha/folio para autollenar el registro.
    const r = await this.client.post<APIResponse<any>>('/accounting/opinion-cumplimiento/leer-pdf', { pdf });
    return r.data;
  }
  async borrarOpinion(id: string) {
    const r = await this.client.delete<APIResponse<any>>(`/accounting/opinion-cumplimiento/${id}`);
    return r.data;
  }
  async verOpinionPdf(id: string) {
    // Vía el cliente axios (su interceptor toma el token de sessionStorage||localStorage).
    // Antes leía el token SOLO de localStorage y, como la sesión vive en sessionStorage,
    // salía 401 → «No se pudo abrir el PDF».
    const r = await this.client.get(`/accounting/opinion-cumplimiento/${id}/pdf`, { responseType: 'blob' });
    window.open(URL.createObjectURL(r.data as Blob), '_blank');
  }
  async getConfigCumplimiento() {
    const r = await this.client.get<APIResponse<any>>('/accounting/opinion-cumplimiento/config');
    return r.data;
  }
  async setConfigCumplimiento(tipo: string, data: any) {
    const r = await this.client.put<APIResponse<any>>(`/accounting/opinion-cumplimiento/config/${tipo}`, data);
    return r.data;
  }
  async descargarCumplimiento(tipo: string) {
    const r = await this.client.post<APIResponse<any>>(`/accounting/opinion-cumplimiento/${tipo}/descargar`, {});
    return r.data;
  }

  /* ── SatGo — consultas por empresa (Servicios SAT) ── */
  async satgoInfoFiscal() {
    // Devuelve lo GUARDADO ({ info, actualizado_at } | null). No toca SatGo.
    const r = await this.client.get<APIResponse<any>>('/satgo/consultas/info-fiscal');
    return r.data;
  }
  async satgoInfoFiscalRefrescar() {
    // Re-consulta en el SAT (CIEC), guarda y devuelve { info, actualizado_at }.
    const r = await this.client.post<APIResponse<any>>('/satgo/consultas/info-fiscal/refrescar', {});
    return r.data;
  }
  async satgoValidarCfdi(params: { re: string; rr: string; tt: string; id: string; fe?: string }) {
    const r = await this.client.get<APIResponse<any>>('/satgo/consultas/validar-cfdi', { params });
    return r.data;
  }
  async satgoDeclaraciones(ejercicio: number, mes = 0) {
    // Descarga el ZIP de declaraciones (CIEC). Usa el cliente axios (token en su interceptor).
    const r = await this.client.get('/satgo/consultas/declaraciones', { params: { ejercicio, mes }, responseType: 'blob' });
    const cd = String((r.headers as any)?.['content-disposition'] || '');
    const m = cd.match(/filename="?([^"]+)"?/);
    const nombre = m ? m[1] : `Declaraciones_${ejercicio}.zip`;
    const url = URL.createObjectURL(r.data as Blob);
    const a = document.createElement('a'); a.href = url; a.download = nombre; a.click();
    URL.revokeObjectURL(url);
  }
  /** Declaraciones descomprimidas de un (ejercicio, mes): PDFs/acuses como data-URL base64.
   *  forzar=true vuelve a bajar de SatGo y sustituye el respaldo (clic en el año). */
  async satgoDeclaracionesContenido(ejercicio: number, mes = 0, forzar = false) {
    const r = await this.client.get<APIResponse<any>>('/satgo/consultas/declaraciones/contenido', { params: { ejercicio, mes, forzar: forzar ? 1 : 0 } });
    return r.data;
  }
  /** Resumen de años ya respaldados (conteo por mes), SIN tocar SatGo — para pintar la cuadrícula al entrar. */
  async satgoDeclaracionesResumen() {
    const r = await this.client.get<APIResponse<any>>('/satgo/consultas/declaraciones/resumen');
    return r.data;
  }
  /** Buzón del SAT: comunicados y notificaciones (por SatGo con e.firma; el portal
   *  puede tardar, por eso el timeout largo). */
  async getBuzonNotificaciones() {
    const r = await this.client.get<APIResponse<any>>('/satgo/consultas/buzon', { timeout: 180000 });
    return r.data;
  }
  /** Contenido de un comunicado del buzón (PDF y/o texto) para leerlo. */
  async getComunicadoContenido(id: string) {
    const r = await this.client.get<APIResponse<any>>(`/satgo/consultas/buzon/comunicado/${encodeURIComponent(id)}`, { timeout: 120000 });
    return r.data;
  }
  // ── PLD / antilavado (LFPIORPI) ──
  async getPldActividades() {
    return (await this.client.get<APIResponse<any>>('/pld/actividades')).data;
  }
  async getPldConfig() {
    return (await this.client.get<APIResponse<any>>('/pld/config')).data;
  }
  async setPldConfig(d: any) {
    return (await this.client.put<APIResponse<any>>('/pld/config', d)).data;
  }
  async getPldTablero() {
    return (await this.client.get<APIResponse<any>>('/pld/tablero')).data;
  }
  async getPldCamposExpediente() {
    return (await this.client.get<APIResponse<any>>('/pld/campos-expediente')).data;
  }
  async getPldExpedientes() {
    return (await this.client.get<APIResponse<any>>('/pld/expedientes')).data;
  }
  async savePldExpediente(d: any) {
    return (await this.client.put<APIResponse<any>>('/pld/expediente', d)).data;
  }
  async getPldAvisoResumen(anio: number, mes: number) {
    return (await this.client.get<APIResponse<any>>('/pld/aviso/resumen', { params: { anio, mes } })).data;
  }
  /** Descarga el BORRADOR del Aviso (XML) del periodo — validar en el Portal SPPLD. */
  async descargarPldAviso(anio: number, mes: number) {
    const r = await this.client.get('/pld/aviso.xml', { params: { anio, mes }, responseType: 'blob' });
    await this.downloadFile(r.data as Blob, `Aviso_PLD_${anio}${String(mes).padStart(2, '0')}.xml`);
  }

  /** Pólizas del respaldo que no se pudieron importar (pendientes). */
  async getPolizasPendientes() {
    const r = await this.client.get<APIResponse<any>>('/accounting/polizas-pendientes');
    return r.data;
  }
  async descartarPolizaPendiente(guid: string) {
    const r = await this.client.delete<APIResponse<any>>(`/accounting/polizas-pendientes/${guid}`);
    return r.data;
  }
  /** Detalle crudo de una pendiente: los movimientos del respaldo, para juzgar si existe / se leyó bien. */
  async getPolizaPendienteDetalle(guid: string) {
    const r = await this.client.get<APIResponse<any>>(`/accounting/polizas-pendientes/${guid}`);
    return r.data;
  }
  /** Mueve los terceros mal colocados a su control correcto (105 cliente / 201 proveedor), renumerados. */
  async reorganizarTerceros() {
    const r = await this.client.post<APIResponse<any>>('/accounting/cuentas/reorganizar-terceros', {});
    return r.data;
  }
  /** Rellena el agrupador SAT de las cuentas que no lo tienen, heredándolo del padre. */
  async asignarAgrupadorFaltante() {
    const r = await this.client.post<APIResponse<any>>('/accounting/cuentas/asignar-agrupador', {});
    return r.data;
  }
  /** Exporta el catálogo de cuentas completo a Excel (para revisar/corregir agrupadores). */
  async descargarCatalogoExcel() {
    const r = await this.client.get('/accounting/cuentas/catalogo/excel', { responseType: 'blob' });
    await this.downloadFile(r.data as Blob, 'Catalogo_de_cuentas.xlsx');
  }
  /** Descarga la plantilla en blanco del catálogo (para armarlo desde cero e importarlo). */
  async descargarPlantillaCatalogo() {
    const r = await this.client.get('/accounting/cuentas/catalogo/plantilla', { responseType: 'blob' });
    await this.downloadFile(r.data as Blob, 'Plantilla_catalogo_cuentas.xlsx');
  }
  /** Importa el catálogo desde Excel (actualiza las que existen y crea las nuevas). */
  async importarCatalogoExcel(fd: FormData) {
    const r = await this.client.post<APIResponse<any>>('/accounting/cuentas/catalogo/importar', fd,
      { headers: { 'Content-Type': 'multipart/form-data' } });
    return r.data;
  }
  /** Importa el catálogo desde el TXT de CPQ (crea las cuentas con su jerarquía). */
  async importarCatalogoTxt(fd: FormData) {
    const r = await this.client.post<APIResponse<any>>('/accounting/cuentas/catalogo/importar-txt', fd,
      { headers: { 'Content-Type': 'multipart/form-data' } });
    return r.data;
  }
  /** Importa pólizas desde el TXT de CPQ (el catálogo debe estar importado antes). */
  async importarPolizasTxt(fd: FormData) {
    const r = await this.client.post<APIResponse<any>>('/accounting/polizas/importar-txt', fd,
      { headers: { 'Content-Type': 'multipart/form-data' } });
    return r.data;
  }
  /** Importa el catálogo de Contalink (CTLK) desde Excel (.xls/.xlsx). */
  async importarContalinkCatalogo(fd: FormData) {
    const r = await this.client.post<APIResponse<any>>('/accounting/contalink/catalogo', fd,
      { headers: { 'Content-Type': 'multipart/form-data' } });
    return r.data;
  }
  /** Importa el libro diario (pólizas) de Contalink (CTLK) desde Excel; el catálogo va primero. */
  async importarContalinkPolizas(fd: FormData) {
    const r = await this.client.post<APIResponse<any>>('/accounting/contalink/polizas', fd,
      { headers: { 'Content-Type': 'multipart/form-data' } });
    return r.data;
  }
  /** Propone el agrupador SAT de las cuentas que no lo tienen (por nombre + herencia). Sólo propone. */
  async proponerAgrupadores() {
    const r = await this.client.get<APIResponse<any>>('/accounting/cuentas/agrupador/propuestas');
    return r.data;
  }
  /** Aplica los agrupadores confirmados: items = [{ id, codigo?, agrupador }]. */
  async aplicarAgrupadores(items: Array<{ id: string; codigo?: string; agrupador: string }>) {
    const r = await this.client.post<APIResponse<any>>('/accounting/cuentas/agrupador/aplicar', { items });
    return r.data;
  }
  async fijarEquivalenciaCuenta(id: string, datos: any) {
    const r = await this.client.put<APIResponse<any>>(`/accounting/cuentas/${id}/equivalencia`, datos);
    return r.data;
  }
  async getCatalogosExternos() {
    const r = await this.client.get<APIResponse<any>>('/accounting/catalogos-externos');
    return r.data;
  }
  /** Lee y revisa una balanza/respaldo SIN guardar nada (análisis de cliente nuevo). */
  async analizarBalanzaRespaldo(fd: FormData) {
    const r = await this.client.post<APIResponse<any>>(
      '/accounting/balanza/analizar', fd,
      { headers: { 'Content-Type': 'multipart/form-data' } });
    return r.data;
  }
  /** Póliza de APERTURA desde la balanza anterior. dryRun=true sólo previsualiza. */
  async polizaApertura(archivo: File, fecha: string, dryRun: boolean) {
    const fd = new FormData();
    fd.append('archivo', archivo);
    fd.append('fecha', fecha);
    const r = await this.client.post<APIResponse<any>>(
      `/accounting/apertura${dryRun ? '?dryRun=true' : ''}`, fd,
      { headers: { 'Content-Type': 'multipart/form-data' } });
    return r.data;
  }
  /* ── Subcuentas por tercero (máscara 000-00-000) ── */
  async generarSubcuentas(direccion: 'emitidos' | 'recibidos') {
    const r = await this.client.post<APIResponse<any>>('/accounting/subcuentas/generar', { direccion });
    return r.data;
  }
  /** Mete en el catálogo de clientes/proveedores los terceros conocidos (RFC + nombre). */
  async capturarTercerosCatalogo(tipo: 'cliente' | 'proveedor') {
    const r = await this.client.post<APIResponse<any>>('/accounting/terceros/capturar-catalogo', { tipo });
    return r.data;
  }
  async getSubcuentasTercero(tipo: 'cliente' | 'proveedor') {
    const r = await this.client.get<APIResponse<any>>('/accounting/subcuentas', { params: { tipo } });
    return r.data;
  }
  async setCodigoSubcuenta(id: string, codigo: string) {
    const r = await this.client.put<APIResponse<any>>(`/accounting/subcuentas/${id}/codigo`, { codigo });
    return r.data;
  }

  /* ── Autofacturación (Anexo 20 / RMF 2.7.3) ── */
  async getEnajenantes() {
    const r = await this.client.get<APIResponse<any>>('/autofactura/enajenantes');
    return r.data;
  }
  async crearEnajenante(data: any) {
    const r = await this.client.post<APIResponse<any>>('/autofactura/enajenantes', data);
    return r.data;
  }
  async actualizarEnajenante(id: string, data: any) {
    const r = await this.client.put<APIResponse<any>>(`/autofactura/enajenantes/${id}`, data);
    return r.data;
  }
  async borrarEnajenante(id: string) {
    const r = await this.client.delete<APIResponse<any>>(`/autofactura/enajenantes/${id}`);
    return r.data;
  }
  async getComprobantesAutofactura(anio?: number, mes?: number) {
    const r = await this.client.get<APIResponse<any>>('/autofactura/comprobantes', { params: { anio, mes } });
    return r.data;
  }
  async getComprobanteAutofactura(id: string) {
    const r = await this.client.get<APIResponse<any>>(`/autofactura/comprobantes/${id}`);
    return r.data;
  }
  async crearComprobanteAutofactura(data: any) {
    const r = await this.client.post<APIResponse<any>>('/autofactura/comprobantes', data);
    return r.data;
  }
  async borrarComprobanteAutofactura(id: string) {
    const r = await this.client.delete<APIResponse<any>>(`/autofactura/comprobantes/${id}`);
    return r.data;
  }
  /** Timbrado gated: hoy responde con el motivo (rol SAT de adquirente + PAC). */
  async timbrarComprobanteAutofactura(id: string) {
    const r = await this.client.post<APIResponse<any>>(`/autofactura/comprobantes/${id}/timbrar`);
    return r.data;
  }

  /* ── Ingresos por producto (ClaveProdServ → 401) ── */
  async getVentasProductos(anio: number, mes: number) {
    const r = await this.client.get<APIResponse<any>>('/accounting/ventas/productos', { params: { anio, mes } });
    return r.data;
  }
  async setVentaProducto(clave: string, descripcion: string | null, cuenta: string | null) {
    const r = await this.client.put<APIResponse<any>>('/accounting/ventas/productos', { clave, descripcion, cuenta });
    return r.data;
  }

  /* ── Compras por producto (ClaveProdServ → 115/601) ── */
  async getComprasProductos(anio: number, mes: number) {
    const r = await this.client.get<APIResponse<any>>('/accounting/compras/productos', { params: { anio, mes } });
    return r.data;
  }
  async setCompraProducto(clave: string, descripcion: string | null, cuenta: string | null) {
    const r = await this.client.put<APIResponse<any>>('/accounting/compras/productos', { clave, descripcion, cuenta });
    return r.data;
  }
  async generarCompras(anio: number, mes: number, todoElAnio = false) {
    const r = await this.client.post<APIResponse<any>>('/accounting/polizas/generar-compras', { anio, mes, todoElAnio });
    return r.data;
  }
  async generarCobrosPagos(anio: number, mes: number, todoElAnio = false) {
    const r = await this.client.post<APIResponse<any>>('/accounting/polizas/generar-cobros-pagos', { anio, mes, todoElAnio });
    return r.data;
  }
  /** Facturas PUE (de contado) del mes pendientes de su póliza de pago. */
  async getPagosPuePendientes(anio: number, mes: number) {
    const r = await this.client.get<APIResponse<any>>(`/accounting/pagos-pue/${anio}/${mes}`);
    return r.data;
  }
  /** Genera el pago de las PUE. asignaciones = {uuid: bancoCuentaId} por factura;
   *  bancoCuentaId es el banco por defecto (cuando la empresa tiene uno solo). */
  async generarPagosPue(anio: number, mes: number, opts: { bancoCuentaId?: string; asignaciones?: Record<string, string> } = {}) {
    const r = await this.client.post<APIResponse<any>>('/accounting/polizas/generar-pagos-pue', { anio, mes, ...opts });
    return r.data;
  }
  /** El asiento contable (pólizas) que tocan un CFDI, por su UUID. */
  async getAsientoPorUuid(uuid: string) {
    const r = await this.client.get<APIResponse<any>>(`/accounting/asiento/${uuid}`);
    return r.data;
  }
  /** Genera el pago de UNA factura PUE contra el banco elegido. */
  async pagarPue(uuid: string, bancoCuentaId: string) {
    const r = await this.client.post<APIResponse<any>>('/accounting/polizas/pago-pue', { uuid, bancoCuentaId });
    return r.data;
  }

  /* ── Pólizas (paso 1: ventas) ── */
  async getPolizas(anio: number, mes: number) {
    const r = await this.client.get<APIResponse<any>>('/accounting/polizas', { params: { anio, mes } });
    return r.data;
  }
  async getPolizasConCuadre(anio: number, mes: number) {
    const r = await this.client.get<APIResponse<any>>('/accounting/polizas/con-cuadre', { params: { anio, mes } });
    return r.data;
  }
  async generarVentas(anio: number, mes: number, todoElAnio = false) {
    const r = await this.client.post<APIResponse<any>>('/accounting/polizas/generar-ventas', { anio, mes, todoElAnio });
    return r.data;
  }
  async borrarPolizasCfdi(anio: number, mes: number) {
    const r = await this.client.delete<APIResponse<any>>('/accounting/polizas/cfdi', { params: { anio, mes } });
    return r.data;
  }
  async crearPolizaManual(datos: any) {
    const r = await this.client.post<APIResponse<any>>('/accounting/polizas/manual', datos);
    return r.data;
  }
  /** Sube XML(s) de facturas recibidas para contabilizarlas (los indexa a cfdi_recibidos). */
  async subirXmlCompra(fd: FormData) {
    const r = await this.client.post<APIResponse<any>>('/accounting/compras/subir-xml', fd,
      { headers: { 'Content-Type': 'multipart/form-data' } });
    return r.data;
  }
  async borrarPoliza(id: string) {
    const r = await this.client.delete<APIResponse<any>>(`/accounting/polizas/${id}`);
    return r.data;
  }
  async editarPoliza(id: string, datos: any) {
    const r = await this.client.put<APIResponse<any>>(`/accounting/polizas/${id}`, datos);
    return r.data;
  }

  /* ── Activos fijos y depreciación ── */
  async getActivos() {
    const r = await this.client.get<APIResponse<any>>('/accounting/activos');
    return r.data;
  }
  async detectarActivos(anio: number, mes: number, desde?: string, hasta?: string) {
    const r = await this.client.get<APIResponse<any>>('/accounting/activos/detectar', { params: { anio, mes, desde, hasta } });
    return r.data;
  }
  async getCedulaActivo(id: string) {
    const r = await this.client.get<APIResponse<any>>(`/accounting/activos/${id}/cedula`);
    return r.data;
  }
  /** Descarga las cédulas de depreciación Y amortización (ambas) en Excel o PDF. */
  async descargarCedulaActivos(formato: 'excel' | 'pdf') {
    const ext = formato === 'pdf' ? 'pdf' : 'xlsx';
    const r = await this.client.get(`/accounting/activos/cedula.${ext}`, { responseType: 'blob' });
    await this.downloadFile(r.data as Blob, `Cedulas_depreciacion_amortizacion.${ext}`);
  }
  async registrarActivo(datos: any) {
    const r = await this.client.post<APIResponse<any>>('/accounting/activos', datos);
    return r.data;
  }
  async registrarActivosDetectados(activos: any[]) {
    const r = await this.client.post<APIResponse<any>>('/accounting/activos/registrar-detectados', { activos });
    return r.data;
  }
  async actualizarActivo(id: string, datos: any) {
    const r = await this.client.put<APIResponse<any>>(`/accounting/activos/${id}`, datos);
    return r.data;
  }
  async borrarActivo(id: string) {
    const r = await this.client.delete<APIResponse<any>>(`/accounting/activos/${id}`);
    return r.data;
  }
  async generarDepreciacion(anio: number, mes: number) {
    const r = await this.client.post<APIResponse<any>>('/accounting/polizas/generar-depreciacion', { anio, mes });
    return r.data;
  }

  /* ── Periodos contables ── */
  async getPeriodosContables(anio: number) {
    const r = await this.client.get<APIResponse<any>>(`/accounting/periodos/${anio}`);
    return r.data;
  }
  async cargarBalanzaEnPeriodo(anio: number, mes: number, fd: FormData) {
    const r = await this.client.post<APIResponse<any>>(
      `/accounting/periodos/${anio}/${mes}/balanza`, fd,
      { headers: { 'Content-Type': 'multipart/form-data' } });
    return r.data;
  }
  async cerrarPeriodoContable(anio: number, mes: number) {
    const r = await this.client.post<APIResponse<any>>(`/accounting/periodos/${anio}/${mes}/cerrar`);
    return r.data;
  }
  async reabrirPeriodoContable(anio: number, mes: number) {
    const r = await this.client.post<APIResponse<any>>(`/accounting/periodos/${anio}/${mes}/reabrir`);
    return r.data;
  }

  /** Todos los estados del mes, desde los saldos del periodo. */
  async getEstadosDelPeriodo(anio: number, mes: number) {
    const r = await this.client.get<APIResponse<any>>(`/accounting/estados/${anio}/${mes}`);
    return r.data;
  }
  async getBalanzaDelPeriodo(anio: number, mes: number) {
    const r = await this.client.get<APIResponse<any>>(`/accounting/estados/${anio}/${mes}/balanza`);
    return r.data;
  }
  /** El mes de trabajo (siguiente al último cerrado): las pantallas arrancan ahí. */
  async getPeriodoActivo() {
    const r = await this.client.get<APIResponse<{ anio: number; mes: number }>>('/accounting/periodo-activo');
    return r.data;
  }
  /* ── Indicadores económicos (INPC, UMA, SM, UMI) ── */
  async getIndicadores() {
    const r = await this.client.get<APIResponse<any>>('/accounting/indicadores');
    return r.data;
  }
  async getInpcSerie(limite = 36) {
    const r = await this.client.get<APIResponse<any>>('/accounting/indicadores/inpc', { params: { limite } });
    return r.data;
  }
  async actualizarInpc() {
    const r = await this.client.post<APIResponse<any>>('/accounting/indicadores/inpc/actualizar', {});
    return r.data;
  }
  /** Importa el INPC desde un archivo del INEGI (CSV/XLSX), sin token. */
  async importarInpcArchivo(file: File) {
    const fd = new FormData(); fd.append('archivo', file);
    const r = await this.client.post<APIResponse<any>>('/accounting/indicadores/inpc/importar', fd,
      { headers: { 'Content-Type': 'multipart/form-data' } });
    return r.data;
  }
  async calcActualizacionRecargos(payload: { monto: number; fechaDebio: string; fechaPago: string }) {
    const r = await this.client.post<APIResponse<any>>('/accounting/indicadores/actualizacion-recargos', payload);
    return r.data;
  }
  async calcAjusteInflacion(payload: { anio: number; saldoPromedioCreditos: number; saldoPromedioDeudas: number }) {
    const r = await this.client.post<APIResponse<any>>('/accounting/indicadores/ajuste-inflacion', payload);
    return r.data;
  }
  async calcPerdidaFiscal(payload: { perdida: number; anioPerdida: number; anioAplicacion: number }) {
    const r = await this.client.post<APIResponse<any>>('/accounting/indicadores/perdida-fiscal', payload);
    return r.data;
  }
  /** Auditoría de cuadre: pólizas descuadradas, balanza y balance↔resultados. mes=0 = año completo. */
  async getValidacionContable(anio: number, mes: number) {
    const r = await this.client.get<APIResponse<any>>(`/accounting/validacion/${anio}/${mes}`);
    return r.data;
  }
  /** Reporte especial: balanza agrupada por agrupador SAT, con cada cuenta contable. */
  async getBalanzaEspecial(anio: number, mes: number) {
    const r = await this.client.get<APIResponse<any>>(`/accounting/reportes-especiales/balanza/${anio}/${mes}`);
    return r.data;
  }
  /** Reporte especial: situación financiera por sección → agrupador → cuenta + fuera de rubro. */
  async getSituacionEspecial(anio: number, mes: number) {
    const r = await this.client.get<APIResponse<any>>(`/accounting/reportes-especiales/situacion/${anio}/${mes}`);
    return r.data;
  }
  async descargarBalanzaEspecial(anio: number, mes: number) {
    const r = await this.client.get(`/accounting/reportes-especiales/balanza/${anio}/${mes}/excel`, { responseType: 'blob' });
    await this.downloadFile(r.data as Blob, `Balanza_especial_${anio}-${String(mes).padStart(2, '0')}.xlsx`);
  }
  async descargarSituacionEspecial(anio: number, mes: number) {
    const r = await this.client.get(`/accounting/reportes-especiales/situacion/${anio}/${mes}/excel`, { responseType: 'blob' });
    await this.downloadFile(r.data as Blob, `Situacion_especial_${anio}-${String(mes).padStart(2, '0')}.xlsx`);
  }
  /** Importa un paquete de respaldo (los JSON del extractor) a la empresa activa. */
  async importarRespaldo(fd: FormData) {
    const r = await this.client.post<APIResponse<any>>('/accounting/contpaqi/importar', fd,
      { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 600000 });
    return r.data;
  }

  /** Recupera los XML/CFDI de un .zip de respaldo CPQ a la bóveda (aparecen en el calendario). */
  async recuperarXmlRespaldo(file: File) {
    const fd = new FormData();
    fd.append('archivo', file);
    const r = await this.client.post<APIResponse<any>>('/accounting/recuperacion-cpq', fd,
      { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 900000 });
    return r.data;
  }
  async getRecuperacionCorridas() {
    const r = await this.client.get<APIResponse<any[]>>('/accounting/recuperacion-cpq/corridas');
    return r.data;
  }
  /** Sube un LOTE de CFDI ya extraídos en el navegador (evita subir el .zip entero). */
  async recuperarXmlLote(archivo: string, xmls: string[]) {
    const r = await this.client.post<APIResponse<any>>('/accounting/recuperacion-cpq/lote',
      { archivo, xmls }, { timeout: 120000 });
    return r.data;
  }
  /** Cierra la corrida: escribe una fila de bitácora con los totales acumulados. */
  async registrarCorridaRecuperacion(archivo: string, resumen: any) {
    const r = await this.client.post<APIResponse<any>>('/accounting/recuperacion-cpq/corrida', { archivo, resumen });
    return r.data;
  }

  /** Descarga la herramienta de importación de respaldos, ya configurada con la
   *  dirección de ESTE servidor (nexo.txt dentro del zip). */
  async descargarHerramientaRespaldo() {
    const r = await this.client.get('/accounting/contpaqi/herramienta', { responseType: 'blob' });
    await this.downloadFile(r.data as Blob, 'Importar respaldo NEXO.zip');
  }

  /** El auxiliar de una cuenta en el mes: sus movimientos con folio de póliza. */
  async getAuxiliarCuenta(cuenta: string, anio: number, mes: number) {
    const r = await this.client.get<APIResponse<any>>(
      `/accounting/estados/${anio}/${mes}/auxiliar`, { params: { cuenta } });
    return r.data;
  }

  /* ── Descargas de reportes contables (Excel / PDF, con encabezado de la casa) ── */
  async descargarBalanzaExcel(anio: number, mes: number) {
    const r = await this.client.get(`/accounting/estados/${anio}/${mes}/balanza/excel`, { responseType: 'blob' });
    await this.downloadFile(r.data as Blob, `Balanza_${anio}-${String(mes).padStart(2, '0')}.xlsx`);
  }
  /** Reporte del ejercicio con 12 columnas (una por mes): balanza / situacion / resultados. */
  async descargarReporteAnual(anio: number, tipo: 'balanza' | 'situacion' | 'resultados' | 'flujo' | 'capital' | 'razones') {
    const r = await this.client.get(`/accounting/estados/${anio}/anual/excel`, { params: { tipo }, responseType: 'blob' });
    const bases: Record<string, string> = {
      resultados: 'Estado_de_resultados', situacion: 'Situacion_financiera', balanza: 'Balanza',
      flujo: 'Flujo_de_efectivo', capital: 'Cambios_en_el_capital', razones: 'Razones_financieras',
    };
    await this.downloadFile(r.data as Blob, `${bases[tipo] || 'Reporte'}_anual_${anio}.xlsx`);
  }
  async descargarBalanzaPdf(anio: number, mes: number) {
    const r = await this.client.get(`/accounting/estados/${anio}/${mes}/balanza/pdf`, { responseType: 'blob' });
    await this.downloadFile(r.data as Blob, `Balanza_${anio}-${String(mes).padStart(2, '0')}.pdf`);
  }
  /** Balance general (contable, no NIF): el árbol del catálogo con saldos al corte. */
  async getBalanceGeneral(anio: number, mes: number) {
    const r = await this.client.get<APIResponse<any>>(`/accounting/balance-general/${anio}/${mes}`);
    return r.data;
  }
  async descargarBalanceGeneral(anio: number, mes: number, formato: 'excel' | 'pdf') {
    const r = await this.client.get(`/accounting/balance-general/${anio}/${mes}/${formato}`, { responseType: 'blob' });
    const ext = formato === 'pdf' ? 'pdf' : 'xlsx';
    await this.downloadFile(r.data as Blob, `Balance_general_${anio}-${String(mes).padStart(2, '0')}.${ext}`);
  }
  /** Estado de resultados contable (no NIF): árbol de cuentas de resultados. */
  async getEstadoResultadosContable(anio: number, mes: number) {
    const r = await this.client.get<APIResponse<any>>(`/accounting/estado-resultados-contable/${anio}/${mes}`);
    return r.data;
  }
  async getEstadoResultadosContableAnual(anio: number) {
    const r = await this.client.get<APIResponse<any>>(`/accounting/estado-resultados-contable-anual/${anio}`);
    return r.data;
  }
  async descargarEstadoResultadosContable(anio: number, mes: number, formato: 'excel' | 'pdf') {
    const r = await this.client.get(`/accounting/estado-resultados-contable/${anio}/${mes}/${formato}`, { responseType: 'blob' });
    const ext = formato === 'pdf' ? 'pdf' : 'xlsx';
    await this.downloadFile(r.data as Blob, `Estado_resultados_${anio}-${String(mes).padStart(2, '0')}.${ext}`);
  }
  async descargarEstadoResultadosContableAnual(anio: number, formato: 'excel' | 'pdf') {
    const r = await this.client.get(`/accounting/estado-resultados-contable-anual/${anio}/${formato}`, { responseType: 'blob' });
    const ext = formato === 'pdf' ? 'pdf' : 'xlsx';
    await this.downloadFile(r.data as Blob, `Estado_resultados_anual_${anio}.${ext}`);
  }
  async descargarAuxiliarExcel(cuenta: string, anio: number, mes: number) {
    const r = await this.client.get(`/accounting/estados/${anio}/${mes}/auxiliar/excel`,
      { params: { cuenta }, responseType: 'blob' });
    await this.downloadFile(r.data as Blob, `Auxiliar_${cuenta}_${anio}-${String(mes).padStart(2, '0')}.xlsx`);
  }
  async descargarAuxiliarPdf(cuenta: string, anio: number, mes: number) {
    const r = await this.client.get(`/accounting/estados/${anio}/${mes}/auxiliar/pdf`,
      { params: { cuenta }, responseType: 'blob' });
    await this.downloadFile(r.data as Blob, `Auxiliar_${cuenta}_${anio}-${String(mes).padStart(2, '0')}.pdf`);
  }
  async descargarSituacion(anio: number, mes: number, formato: 'excel' | 'pdf') {
    const r = await this.client.get(`/accounting/estados/${anio}/${mes}/situacion/${formato}`, { responseType: 'blob' });
    await this.downloadFile(r.data as Blob, `Situacion_${anio}-${String(mes).padStart(2, '0')}.${formato === 'pdf' ? 'pdf' : 'xlsx'}`);
  }
  async descargarResultados(anio: number, mes: number, formato: 'excel' | 'pdf') {
    const r = await this.client.get(`/accounting/estados/${anio}/${mes}/resultados/${formato}`, { responseType: 'blob' });
    await this.downloadFile(r.data as Blob, `Resultados_${anio}-${String(mes).padStart(2, '0')}.${formato === 'pdf' ? 'pdf' : 'xlsx'}`);
  }
  async descargarFlujo(anio: number, mes: number, formato: 'excel' | 'pdf') {
    const r = await this.client.get(`/accounting/estados/${anio}/${mes}/flujo/${formato}`, { responseType: 'blob' });
    await this.downloadFile(r.data as Blob, `Flujo_${anio}-${String(mes).padStart(2, '0')}.${formato === 'pdf' ? 'pdf' : 'xlsx'}`);
  }
  async descargarCambios(anio: number, mes: number, formato: 'excel' | 'pdf') {
    const r = await this.client.get(`/accounting/estados/${anio}/${mes}/cambios/${formato}`, { responseType: 'blob' });
    await this.downloadFile(r.data as Blob, `CambiosCapital_${anio}-${String(mes).padStart(2, '0')}.${formato === 'pdf' ? 'pdf' : 'xlsx'}`);
  }
  async descargarRazones(anio: number, mes: number, formato: 'excel' | 'pdf') {
    const r = await this.client.get(`/accounting/estados/${anio}/${mes}/razones/${formato}`, { responseType: 'blob' });
    await this.downloadFile(r.data as Blob, `Razones_${anio}-${String(mes).padStart(2, '0')}.${formato === 'pdf' ? 'pdf' : 'xlsx'}`);
  }

  /* ── Reportes fiscales: DIOT y Contabilidad Electrónica (Anexo 24) ── */
  /** DIOT del mes: compras por proveedor con IVA por tasa e IVA retenido. */
  async getDiot(anio: number, mes: number) {
    const r = await this.client.get<APIResponse<any>>(`/accounting/diot/${anio}/${mes}`);
    return r.data;
  }
  /** Archivo .txt de carga masiva DIOT 2025 para el portal del SAT. */
  async descargarDiotBatch(anio: number, mes: number, opts: {
    tipoOperacion?: string; region?: 'none' | 'norte' | 'sur'; proporcion?: boolean;
  } = {}) {
    const r = await this.client.get(`/accounting/diot/${anio}/${mes}/batch`, {
      params: {
        tipoOperacion: opts.tipoOperacion || '85',
        region: opts.region || 'none',
        proporcion: opts.proporcion ? 'true' : 'false',
      },
      responseType: 'blob',
    });
    await this.downloadFile(r.data as Blob,
      this.nombreDeHeader(r, `DIOT_${anio}${String(mes).padStart(2, '0')}.txt`));
  }
  /** El nombre del archivo lo pone el SAT (RFC+periodo); se lee del header. */
  private nombreDeHeader(r: any, alterno: string): string {
    const cd = String(r.headers?.['content-disposition'] || '');
    const m = /filename="?([^";]+)"?/i.exec(cd);
    return m?.[1] || alterno;
  }
  /** Catálogo de cuentas XML (Contabilidad Electrónica). */
  async descargarCatalogoElectronico(anio: number, mes: number) {
    const r = await this.client.get(
      `/accounting/contabilidad-electronica/catalogo/${anio}/${mes}`, { responseType: 'blob' });
    await this.downloadFile(r.data as Blob,
      this.nombreDeHeader(r, `Catalogo_${anio}-${String(mes).padStart(2, '0')}.xml`));
  }
  /** Balanza de comprobación XML (Contabilidad Electrónica). tipoEnvio N normal / C complementaria. */
  async descargarBalanzaElectronica(anio: number, mes: number, tipoEnvio: 'N' | 'C' = 'N') {
    const r = await this.client.get(
      `/accounting/contabilidad-electronica/balanza/${anio}/${mes}`,
      { params: { tipoEnvio }, responseType: 'blob' });
    await this.downloadFile(r.data as Blob,
      this.nombreDeHeader(r, `Balanza_${anio}-${String(mes).padStart(2, '0')}B${tipoEnvio}.xml`));
  }
  /** Vista previa (JSON) del catálogo / balanza de Contabilidad Electrónica. */
  async getCatalogoElectronicoPreview(anio: number, mes: number) {
    const r = await this.client.get<APIResponse<any>>(`/accounting/contabilidad-electronica/catalogo/${anio}/${mes}/preview`);
    return r.data;
  }
  async getBalanzaElectronicaPreview(anio: number, mes: number) {
    const r = await this.client.get<APIResponse<any>>(`/accounting/contabilidad-electronica/balanza/${anio}/${mes}/preview`);
    return r.data;
  }
  /** Deriva/actualiza la balanza del mes desde las pólizas (journal_lines). */
  async actualizarBalanzaDesdePolizas(anio: number, mes: number) {
    const r = await this.client.post<APIResponse<any>>(`/accounting/periodos/${anio}/${mes}/desde-polizas`);
    return r.data;
  }
  /** Reconstruye la balanza de los 12 meses del año desde las pólizas, en orden. */
  async actualizarBalanzaAnualDesdePolizas(anio: number) {
    const r = await this.client.post<APIResponse<any>>(`/accounting/periodos/${anio}/desde-polizas-anual`);
    return r.data;
  }
  /** Reconstrucción INTEGRAL: reconstruye la balanza de TODOS los ejercicios con
   *  pólizas de un tirón (tras importar un respaldo). */
  async reconstruirIntegral() {
    const r = await this.client.post<APIResponse<any>>('/accounting/periodos/reconstruir-integral');
    return r.data;
  }

  /* ── Programación de la descarga del SAT ── */
  async getProgramacionSat() {
    const r = await this.client.get<APIResponse<any>>('/sat-descarga/programacion');
    return r.data;
  }

  /* ── Vista fiscal de una persona física desde sus XML (nómina + ingresos/egresos) ── */
  async getNominaPF(anio: number, mes = 0) {
    const r = await this.client.get<APIResponse<any>>('/sat-descarga/pf/nomina', { params: { anio, mes } });
    return r.data;
  }
  async getResumenPF(anio: number) {
    const r = await this.client.get<APIResponse<any>>('/sat-descarga/pf/resumen', { params: { anio } });
    return r.data;
  }
  /** Respaldo (.zip) de los XML del SAT almacenados (la fuente de la verdad). */
  async descargarRespaldoXml(anio?: number, mes?: number, direccion?: string) {
    const r = await this.client.get('/sat-descarga/respaldo.zip',
      { params: { anio, mes: mes || undefined, direccion: direccion || undefined }, responseType: 'blob' });
    const nombre = `Respaldo_XML_${direccion || 'todos'}_${anio || 'todo'}${mes ? '-' + String(mes).padStart(2, '0') : ''}.zip`;
    await this.downloadFile(r.data as Blob, nombre);
  }
  async guardarProgramacionSat(cfg: any) {
    const r = await this.client.put<APIResponse<any>>('/sat-descarga/programacion', cfg);
    return r.data;
  }
  async crearDiarioSat() {
    const r = await this.client.post<APIResponse<any>>('/sat-descarga/diario');
    return r.data;
  }
  /** Avanza la descarga del SAT de TODAS mis empresas con e.firma vigente (en 2º plano). */
  async avanzarDescargaTodas() {
    const r = await this.client.post<APIResponse<{ empresas: number }>>('/sat-descarga/avanzar-todas');
    return r.data;
  }
  async reiniciarSatDescarga() {
    const r = await this.client.post<APIResponse<any>>('/sat-descarga/reiniciar');
    return r.data;
  }
  /** Re-verifica por el servicio fiscal el estatus de los CFDI guardados y marca cancelados. */
  async verificarCanceladosSat(limite = 50) {
    const r = await this.client.post<APIResponse<any>>('/sat-descarga/verificar-cancelados', { limite });
    return r.data;
  }
  /** Quita de la lista los trabajos terminados/cancelados (los XML se conservan). */
  async limpiarTrabajosTerminados() {
    const r = await this.client.post<APIResponse<any>>('/sat-descarga/limpiar-terminados');
    return r.data;
  }
  /** Borra SÓLO las descargas pendientes/en curso (los XML ya bajados se conservan). */
  async borrarPendientesSat() {
    const r = await this.client.post<APIResponse<any>>('/sat-descarga/borrar-pendientes');
    return r.data;
  }
  async reintentarSatAtoradas() {
    const r = await this.client.post<APIResponse<any>>('/sat-descarga/reintentar');
    return r.data;
  }
  /** Prueba de solo lectura: autentica la e.firma y pregunta al SAT por las en vuelo. */
  async diagnosticoSat() {
    const r = await this.client.post<APIResponse<any>>('/sat-descarga/diagnostico');
    return r.data;
  }
  async crearEjercicioSat(ejercicio: number, opts: any = {}) {
    const r = await this.client.post<APIResponse<any>>('/sat-descarga/ejercicio',
      { ejercicio, ...opts });
    return r.data;
  }
  /** Estado por día (calendario) de la descarga de XML. */
  async getCoberturaSat(anio: number, direccion: 'recibidos' | 'emitidos') {
    const r = await this.client.get<APIResponse<any>>('/sat-descarga/cobertura', { params: { anio, direccion } });
    return r.data;
  }
  /** Cobertura combinada (emitidos + recibidos) para un solo calendario. */
  async getCoberturaCombinada(anio: number) {
    const r = await this.client.get<APIResponse<any>>('/sat-descarga/cobertura-combinada', { params: { anio } });
    return r.data;
  }
  /** Crea trabajos para los meses del año con días faltantes. */
  async llenarHuecosSat(anio: number, direccion: 'recibidos' | 'emitidos') {
    const r = await this.client.post<APIResponse<any>>('/sat-descarga/llenar-huecos', { anio, direccion });
    return r.data;
  }

  async evaluarNif(fd: FormData) {
    const r = await this.client.post<APIResponse<any>>('/accounting/nif/evaluar', fd,
      { headers: { 'Content-Type': 'multipart/form-data' } });
    return r.data;
  }
  async getReglasNif() {
    const r = await this.client.get<APIResponse<any>>('/accounting/nif/reglas');
    return r.data;
  }

  async getNormasNif() {
    const r = await this.client.get<APIResponse<any>>('/accounting/nif');
    return r.data;
  }
  async getFaltantesAnexo24() {
    const r = await this.client.get<APIResponse<any>>('/accounting/referencias/faltantes');
    return r.data;
  }
  async sembrarReferenciasContables() {
    const r = await this.client.post<APIResponse<any>>('/accounting/referencias/sembrar');
    return r.data;
  }
  async activarContabilidad(datos: any) {
    const r = await this.client.post<APIResponse<any>>('/accounting/activar', datos);
    return r.data;
  }

  /** Los bancos con su clave de 3 dígitos (catálogo SPEI/ABM). */
  async getCatalogoBancos() {
    const r = await this.client.get<APIResponse<any>>('/treasury/bancos/catalogo');
    return r.data;
  }
  async borrarEstadoDeCuenta(estadoId: string) {
    const r = await this.client.delete<APIResponse<any>>(`/treasury/bancos/estados/${estadoId}`);
    return r.data;
  }

  /** El archivo puente: el estado de cuenta como CSV, para contabilidad. */
  async descargarCsvEstado(estadoId: string, nombre: string) {
    const r = await this.client.get(`/treasury/bancos/estados/${estadoId}/csv`,
      { responseType: 'blob' });
    await this.downloadFile(r.data as Blob, nombre);
  }

  async getControlMensual(cuentaId: string, anio?: number) {
    const r = await this.client.get<APIResponse<any>>(
      `/treasury/bancos/cuentas/${cuentaId}/control`, { params: { anio } });
    return r.data;
  }
  /** El detalle de un estado de cuenta: saldos y movimientos. */
  async getDetalleEstadoBanco(estadoId: string) {
    const r = await this.client.get<APIResponse<any>>(`/treasury/bancos/estados/${estadoId}`);
    return r.data;
  }
  /** El estado de cuenta como Excel. */
  async descargarExcelEstado(estadoId: string, nombre: string) {
    const r = await this.client.get(`/treasury/bancos/estados/${estadoId}/excel`, { responseType: 'blob' });
    await this.downloadFile(r.data as Blob, nombre);
  }

  async listPayments() {
    const r = await this.client.get('/payments');
    return r.data;
  }

  async getInvoicePayments(invoiceId: string) {
    const r = await this.client.get(`/payments/by-invoice/${invoiceId}`);
    return r.data;
  }

  async paymentPDF(paymentId: string, inline = false) {
    const r = await this.client.get(`/payments/${paymentId}/pdf${inline ? '?inline=1' : ''}`, {
      responseType: 'blob',
    });
    return r.data;
  }

  async paymentXML(paymentId: string) {
    const r = await this.client.get(`/payments/${paymentId}/xml`, { responseType: 'blob' });
    return r.data;
  }

  async creditNotePDF(noteId: string, inline = false) {
    const r = await this.client.get(`/credit-notes/${noteId}/pdf${inline ? '?inline=1' : ''}`, {
      responseType: 'blob',
    });
    return r.data;
  }

  async creditNoteXML(noteId: string) {
    const r = await this.client.get(`/credit-notes/${noteId}/xml`, { responseType: 'blob' });
    return r.data;
  }

  /* ───────────── Notas de Crédito ───────────── */

  async getCreditNoteMotivos() {
    const r = await this.client.get('/credit-notes/motivos');
    return r.data;
  }

  async listCreditNotes() {
    const r = await this.client.get('/credit-notes');
    return r.data;
  }

  async createCreditNote(data: {
    customerId: string;
    invoiceId: string;
    tipoRelacion?: string;
    motivo?: string;
    amount?: number;
    discountPercent?: number;
    iva?: number;
    currency?: string;
    applyToInvoice?: boolean;
  }) {
    const r = await this.client.post('/credit-notes', data);
    return r.data;
  }

  async getInvoiceBalance(invoiceId: string) {
    const r = await this.client.get(`/invoices/${invoiceId}/balance`);
    return r.data;
  }

  async getDashboardSummary() {
    const r = await this.client.get('/invoices/dashboard/summary');
    return r.data;
  }

  /** Consumo de timbres del mes en curso vs cap del plan (iguala = 100). */
  async getMonthlyUsage() {
    const r = await this.client.get('/archive/usage/current-month');
    return r.data;
  }

  /** Solo ADMIN — descarga paquete fiscal de una compañía específica. */
  async adminDownloadPackage(opts: {
    companyId: string;
    from?: string; to?: string;
    format?: 'xml' | 'both';
    limit?: number;
  }) {
    const params = new URLSearchParams({
      companyId: opts.companyId,
      format: opts.format || 'xml',
      limit:  String(opts.limit || 100),
      ...(opts.from ? { from: opts.from } : {}),
      ...(opts.to   ? { to:   opts.to   } : {}),
    });
    const r = await this.client.get(`/archive/admin/invoices.zip?${params.toString()}`, {
      responseType: 'blob',
    });
    return r.data;
  }

  /** Listado de compañías — solo ADMIN. Si el backend aún no lo expone, lo pedimos por SQL helper. */
  async listCompanies() {
    const r = await this.client.get('/companies').catch(() => null);
    return r?.data || { data: { companies: [] } };
  }

  /* ───────────── SUPER ADMIN ───────────── */
  async adminListUsers(params: { search?: string; companyId?: string; limit?: number } = {}) {
    const r = await this.client.get('/admin/users', { params });
    return r.data;
  }
  async adminCreateUser(data: { email: string; firstName: string; lastName: string; role: string; companyId?: string; workGroup?: string; extraModules?: string[] }) {
    const r = await this.client.post('/admin/users', data);
    return r.data;
  }
  async adminUpdateUser(id: string, data: any) {
    const r = await this.client.put(`/admin/users/${id}`, data);
    return r.data;
  }
  async adminResetPassword(id: string) {
    const r = await this.client.post(`/admin/users/${id}/reset-password`, {});
    return r.data;
  }
  /** Reseteo PROTEGIDO de un super admin: paso 1 (manda código al correo de seguridad). */
  async adminResetSuperSolicitar(id: string) {
    return (await this.client.post(`/admin/users/${id}/reset-super/solicitar`, {})).data;
  }
  /** Paso 2: confirma con el código de 12 dígitos y devuelve la contraseña temporal. */
  async adminResetSuperConfirmar(id: string, codigo: string) {
    return (await this.client.post(`/admin/users/${id}/reset-super/confirmar`, { codigo })).data;
  }
  async adminDisableUser(id: string) {
    const r = await this.client.post(`/admin/users/${id}/disable`, {});
    return r.data;
  }
  async adminEnableUser(id: string) {
    const r = await this.client.post(`/admin/users/${id}/enable`, {});
    return r.data;
  }
  /** Borrado DEFINITIVO (solo usuarios deshabilitados). Exige el correo exacto. */
  async adminDeleteUser(id: string, confirmEmail: string) {
    const r = await this.client.delete(`/admin/users/${id}`, { data: { confirmEmail } });
    return r.data;
  }
  async adminEnterCompany(companyId: string) {
    const r = await this.client.post<{ data: { token: string; user: any; company: any } }>(`/admin/companies/${companyId}/enter`, {});
    return r.data;
  }
  async adminImpersonate(id: string) {
    const r = await this.client.post(`/admin/users/${id}/impersonate`, {});
    return r.data;
  }

  /* ───────────── CFDI Import (XML wizard) ───────────── */
  async cfdiPreview(xmlBase64: string) {
    const r = await this.client.post('/cfdi-import/preview', { xmlBase64 });
    return r.data;
  }
  async cfdiCommit(payload: any) {
    const r = await this.client.post('/cfdi-import/commit', payload);
    return r.data;
  }
  async cfdiImportHistory() {
    const r = await this.client.get('/cfdi-import/history?limit=50');
    return r.data;
  }

  /* ───────────── Proveedores ───────────── */
  async updateSupplierCredit(id: string, data: {
    creditDays?: number;
    creditLine?: number;
    paymentConditions?: string;
    supplierRating?: number;
    deliveryDaysAvg?: number;
  }) {
    const r = await this.client.put<APIResponse<any>>(`/suppliers/${id}/credit`, data);
    return r.data;
  }

  /* ───────────── Tesorería (Fase 6 ALMACEN) ───────────── */
  async getTreasuryPayments(params?: {
    status?: string; supplierId?: string; from?: string; to?: string;
    /** true = sólo lo que aún no está en ninguna remesa. */
    sinRemesa?: boolean;
  }) {
    const r = await this.client.get<APIResponse<any>>('/treasury/payments', { params });
    return r.data;
  }

  /** Cruce de nuestros terceros contra el padrón del 69-B. */
  async get69B() {
    const r = await this.client.get<APIResponse<any>>('/auditoria/69b');
    return r.data;
  }
  /** Carga el archivo que publica el SAT. Sólo ADMIN. */
  async importar69B(archivo: File) {
    const fd = new FormData();
    fd.append('archivo', archivo);
    const r = await this.client.post<APIResponse<any>>('/auditoria/69b/importar', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return r.data;
  }

  /** Baja la lista del 69-B del portal del SAT y la carga. Sólo ADMIN. */
  async actualizar69BDesdeElSat() {
    const r = await this.client.post<APIResponse<any>>('/auditoria/69b/actualizar', {}, {
      /* Son 4.7 MB y catorce mil renglones: el tiempo de espera por omisión se
       * queda corto y la pantalla mostraría un error de red sobre una carga que
       * en realidad terminó bien. */
      timeout: 180_000,
    });
    return r.data;
  }

  /* ───────────── Nómina ───────────── */
  async getNominaCatalogos() {
    const r = await this.client.get<APIResponse<any>>('/nomina/catalogos');
    return r.data;
  }
  /* Conceptos de nómina → cuenta (config de la póliza de pasivo) */
  async getConceptosCuentaNomina() {
    const r = await this.client.get<APIResponse<any>>('/nomina/conceptos-cuenta');
    return r.data;
  }
  async setConceptoCuentaNomina(grupo: string, clave: string, cuenta: string | null) {
    const r = await this.client.put<APIResponse<any>>('/nomina/conceptos-cuenta', { grupo, clave, cuenta });
    return r.data;
  }
  /* ── Póliza de pasivo de nómina (finiquitos timbrados) ── */
  async getFiniquitosParaPoliza() {
    const r = await this.client.get<APIResponse<any>>('/nomina/poliza/finiquitos');
    return r.data;
  }
  async getPolizaNomina(reciboId: string) {
    const r = await this.client.get<APIResponse<any>>(`/nomina/poliza/${reciboId}`);
    return r.data;
  }
  async generarPolizaNomina(reciboId: string) {
    const r = await this.client.post<APIResponse<any>>(`/nomina/poliza/${reciboId}/generar`);
    return r.data;
  }
  /* ── Póliza de nómina ORDINARIA (agregada por periodo) ── */
  async getPeriodosOrdinariosPoliza() {
    const r = await this.client.get<APIResponse<any>>('/nomina/poliza-periodos');
    return r.data;
  }
  async getPolizaPeriodoNomina(periodoId: string) {
    const r = await this.client.get<APIResponse<any>>(`/nomina/poliza-periodo/${periodoId}`);
    return r.data;
  }
  async generarPolizaPeriodoNomina(periodoId: string) {
    const r = await this.client.post<APIResponse<any>>(`/nomina/poliza-periodo/${periodoId}/generar`);
    return r.data;
  }
  async getNominaParametros() {
    const r = await this.client.get<APIResponse<any>>('/nomina/parametros');
    return r.data;
  }
  async guardarNominaParametros(datos: any) {
    const r = await this.client.put<APIResponse<any>>('/nomina/parametros', datos);
    return r.data;
  }
  async getNominaPuestos() {
    const r = await this.client.get<APIResponse<any>>('/nomina/puestos');
    return r.data;
  }
  async crearNominaPuesto(nombre: string, riesgo_puesto?: string) {
    const r = await this.client.post<APIResponse<any>>('/nomina/puestos', { nombre, riesgo_puesto });
    return r.data;
  }
  async getNominaDepartamentos() {
    const r = await this.client.get<APIResponse<any>>('/nomina/departamentos');
    return r.data;
  }
  async crearNominaDepartamento(nombre: string) {
    const r = await this.client.post<APIResponse<any>>('/nomina/departamentos', { nombre });
    return r.data;
  }

  /* ── Bitácora y entregas del expediente ── */
  async getBitacora(empleadoId: string) {
    const r = await this.client.get<APIResponse<any>>(`/nomina/empleados/${empleadoId}/bitacora`);
    return r.data;
  }
  async crearNotaBitacora(datos: any) {
    const r = await this.client.post<APIResponse<any>>('/nomina/bitacora', datos);
    return r.data;
  }
  async cancelarNotaBitacora(id: string, motivo: string) {
    const r = await this.client.post<APIResponse<any>>(`/nomina/bitacora/${id}/cancelar`, { motivo });
    return r.data;
  }
  async getEntregas(empleadoId: string) {
    const r = await this.client.get<APIResponse<any>>(`/nomina/empleados/${empleadoId}/entregas`);
    return r.data;
  }
  async registrarEntrega(datos: any) {
    const r = await this.client.post<APIResponse<any>>('/nomina/entregas', datos);
    return r.data;
  }
  /** La foto (evidencia) de una entrega, data-URL para <img src>. */
  async getFotoEntrega(id: string) {
    const r = await this.client.get<APIResponse<{ foto: string | null }>>(`/nomina/entregas/${id}/foto`);
    return r.data;
  }
  async registrarDevolucion(id: string, datos: any) {
    const r = await this.client.post<APIResponse<any>>(`/nomina/entregas/${id}/devolucion`, datos);
    return r.data;
  }

  /* ── Periodos y prenómina ── */
  async getPlantillaPorTipo() {
    const r = await this.client.get<APIResponse<any>>('/nomina/plantilla-por-tipo');
    return r.data;
  }
  async getPeriodosNomina(params: { anio?: number; tipo?: string } = {}) {
    const r = await this.client.get<APIResponse<any>>('/nomina/periodos', { params });
    return r.data;
  }
  async generarPeriodosNomina(tipo: string, anio: number, fechaArranque?: string) {
    const r = await this.client.post<APIResponse<any>>('/nomina/periodos/generar', {
      tipo, anio, fechaArranque,
    });
    return r.data;
  }
  async crearPeriodoEspecial(datos: any) {
    const r = await this.client.post<APIResponse<any>>('/nomina/periodos/especial', datos);
    return r.data;
  }
  /** Quiénes entran a un periodo especial. Lista vacía = toda la plantilla. */
  async getParticipantes(periodoId: string) {
    const r = await this.client.get<APIResponse<any>>(
      `/nomina/periodos/${periodoId}/participantes`);
    return r.data;
  }
  async fijarParticipantes(periodoId: string, empleadoIds: string[]) {
    const r = await this.client.put<APIResponse<any>>(
      `/nomina/periodos/${periodoId}/participantes`, { empleadoIds });
    return r.data;
  }

  /** Baja la prenómina como Excel, con lo capturado en la rejilla. */
  async descargarPrenominaExcel(periodoId: string, captura: any[], nombre: string) {
    const r = await this.client.post(
      `/nomina/prenomina/${periodoId}/excel`, { captura },
      { responseType: 'blob', timeout: 120_000 }
    );
    await this.downloadFile(r.data as Blob, nombre);
  }

  /** El reloj checador arma la nómina: aplica faltas (y detecta retardos) del periodo. */
  async prenominaCargarChecador(periodoId: string) {
    const r = await this.client.post<APIResponse<any>>(`/nomina/prenomina/${periodoId}/incidencias-checador`, {});
    return r.data;
  }
  /** Asistencia completa a todos: quita las faltas del checador, deja días completos. */
  async prenominaAsistenciaDefault(periodoId: string) {
    const r = await this.client.post<APIResponse<any>>(`/nomina/prenomina/${periodoId}/asistencia-default`, {});
    return r.data;
  }

  /**
   * IMSS · IDSE — arma y descarga el TXT de movimientos afiliatorios.
   *
   * Pide la respuesta como blob (es un archivo), pero cuando el backend rechaza
   * el lote contesta JSON con la lista de lo que falta. Axios entrega ESE JSON
   * también como blob, así que hay que leerlo y sacar el mensaje: sin esto, un
   * "captura el NSS de fulano" llegaría a la pantalla como un error genérico.
   */
  async generarIdse(body: {
    tipo: 'ALTA' | 'BAJA' | 'MODIFICACION';
    movimientos: any[];
    guia?: string; tipoTrabajador?: string; tipoSalario?: string; jornada?: string;
  }) {
    try {
      const r = await this.client.post('/nomina/imss/idse', body, { responseType: 'blob' });
      const cd = String(r.headers['content-disposition'] || '');
      const m = /filename="?([^"]+)"?/.exec(cd);
      const nombre = m?.[1] || `IDSE_${body.tipo}_${new Date().toISOString().slice(0, 10)}.txt`;
      await this.downloadFile(r.data as Blob, nombre);
    } catch (e: any) {
      const data = e?.response?.data;
      if (data instanceof Blob) {
        const txt = await data.text();
        let msg = txt;
        try { const j = JSON.parse(txt); msg = j.message || j.error || txt; } catch { /* no era JSON */ }
        throw new Error(msg);
      }
      throw e;
    }
  }

  /** Constructor unificado: UN archivo con movimientos de tipos mezclados. */
  async generarIdseMixto(body: {
    movimientos: Array<{ empleadoId: string; tipo: string; fecha: string; sbc?: number;
      umf?: string; claveTrabajador?: string; curp?: string; causaBaja?: string }>;
    guia?: string; tipoTrabajador?: string; tipoSalario?: string; jornada?: string;
  }) {
    try {
      const r = await this.client.post('/nomina/imss/idse/mixto', body, { responseType: 'blob' });
      const cd = String(r.headers['content-disposition'] || '');
      const m = /filename="?([^"]+)"?/.exec(cd);
      await this.downloadFile(r.data as Blob, m?.[1] || `IDSE_movimientos_${new Date().toISOString().slice(0, 10)}.txt`);
    } catch (e: any) {
      const data = e?.response?.data;
      if (data instanceof Blob) {
        const txt = await data.text();
        let msg = txt;
        try { const j = JSON.parse(txt); msg = j.message || j.error || txt; } catch { /* no era JSON */ }
        throw new Error(msg);
      }
      throw e;
    }
  }

  /** Valida un TXT del IDSE contra las posiciones de la guía. */
  async validarIdse(contenido: string) {
    const r = await this.client.post<APIResponse<any>>('/nomina/imss/idse/validar', { contenido });
    return r.data;
  }

  /** Movimientos que la baja/reingreso encolaron para el IDSE. */
  async getIdsePendientes() {
    const r = await this.client.get<APIResponse<any>>('/nomina/imss/idse/pendientes');
    return r.data;
  }
  async descartarIdsePendiente(id: string) {
    const r = await this.client.delete<APIResponse<any>>(`/nomina/imss/idse/pendientes/${id}`);
    return r.data;
  }
  /** Genera UN archivo con los pendientes elegidos (tipos mezclados) y lo descarga. */
  async generarIdseDesdePendientes(ids: string[], cfg: any = {}) {
    try {
      const r = await this.client.post('/nomina/imss/idse/pendientes/generar', { ids, ...cfg }, { responseType: 'blob' });
      const cd = String(r.headers['content-disposition'] || '');
      const m = /filename="?([^"]+)"?/.exec(cd);
      await this.downloadFile(r.data as Blob, m?.[1] || `IDSE_movimientos_${new Date().toISOString().slice(0, 10)}.txt`);
    } catch (e: any) {
      const data = e?.response?.data;
      if (data instanceof Blob) {
        const txt = await data.text();
        let msg = txt;
        try { const j = JSON.parse(txt); msg = j.message || j.error || txt; } catch { /* no era JSON */ }
        throw new Error(msg);
      }
      throw e;
    }
  }
  async getIdseEnviados() {
    const r = await this.client.get<APIResponse<any>>('/nomina/imss/idse/enviados');
    return r.data;
  }
  async marcarIdseEnviados(ids: string[]) {
    const r = await this.client.post<APIResponse<any>>('/nomina/imss/idse/pendientes/enviados', { ids });
    return r.data;
  }
  async regresarIdsePendientes(ids: string[]) {
    const r = await this.client.post<APIResponse<any>>('/nomina/imss/idse/enviados/regresar', { ids });
    return r.data;
  }

  /** La bitácora de archivos IDSE generados, con la foto de sus movimientos. */
  async getIdseLotes() {
    const r = await this.client.get<APIResponse<any>>('/nomina/imss/idse/lotes');
    return r.data;
  }
  /** Sube el acuse (PDF que devuelve el portal del IDSE) a un lote. */
  async subirIdseAcuse(loteId: string, file: File) {
    const fd = new FormData();
    fd.append('acuse', file);
    const r = await this.client.post<APIResponse<any>>(
      `/nomina/imss/idse/lotes/${loteId}/acuse`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    return r.data;
  }
  /** Abre el acuse guardado de un lote en una pestaña (o lo descarga si el navegador la bloquea). */
  async verIdseAcuse(loteId: string) {
    const r = await this.client.get(`/nomina/imss/idse/lotes/${loteId}/acuse`, { responseType: 'blob' });
    const blob = r.data as Blob;
    const url = URL.createObjectURL(new Blob([blob], { type: blob.type || 'application/pdf' }));
    const w = window.open(url, '_blank');
    if (!w) await this.downloadFile(new Blob([blob]), 'acuse-idse.pdf');
  }
  async borrarIdseLote(loteId: string) {
    const r = await this.client.delete<APIResponse<any>>(`/nomina/imss/idse/lotes/${loteId}`);
    return r.data;
  }

  /** Cierra el periodo: congela los recibos y genera los XML. ESCRIBE. */
  async cerrarPeriodoNomina(periodoId: string, captura: any[]) {
    const r = await this.client.post<APIResponse<any>>(
      `/nomina/prenomina/${periodoId}/cerrar`, { captura }, { timeout: 180_000 }
    );
    return r.data;
  }

  /** El ejercicio fiscal con sus tablas: tarifa del Art. 96, subsidio, UMA, UMI. */
  async getEjercicioNomina(anio: number) {
    const r = await this.client.get<APIResponse<any>>(`/nomina/ejercicios/${anio}`);
    return r.data;
  }

  /** Tablas del ISR para consulta: las 12 acumuladas por mes, la anual y las de cada periodicidad. */
  async getTablasIsr(anio: number) {
    const r = await this.client.get<APIResponse<any>>(`/nomina/tablas-isr/${anio}`);
    return r.data;
  }

  /** Cuánto grava y cuánto exenta lo capturado. NO guarda. */
  async partirConceptos(body: {
    periodoId: string; empleadoId: string;
    lado: 'ingresos' | 'egresos';
    lineas: Array<{ clave: string; importe: number; gravadoManual?: number }>;
  }) {
    const r = await this.client.post<APIResponse<any>>('/nomina/conceptos/partir', body);
    return r.data;
  }

  /** Finiquito y liquidación de quien se va. NO escribe. */
  async getFiniquito(empleadoId: string, params: {
    fechaBaja: string; vacacionesYaDisfrutadas?: number; diasPendientesDePagar?: number;
    indemnizacionDias?: number;
  }) {
    const r = await this.client.get<APIResponse<any>>(
      `/nomina/empleados/${empleadoId}/finiquito`, { params }
    );
    return r.data;
  }

  /** Qué versión del backend está viva. Va fuera de /api: es del servidor. */
  async getSalud(): Promise<any> {
    const base = (this.client.defaults.baseURL || '').replace(/\/api\/v\d+\/?$/, '');
    const r = await this.client.get('/health', { baseURL: base || undefined });
    return r.data;
  }

  /* ── Reportes de nómina ── */

  /** Qué periodos CERRADOS hay, para no ofrecer rangos vacíos. */
  async getPeriodosParaReporte(anio: number) {
    const r = await this.client.get<APIResponse<any>>('/nomina/reportes/periodos', {
      params: { anio },
    });
    return r.data;
  }

  /** Uno de los cuatro: prenomina | cfdi | isr | imss. */
  async getReporteNomina(que: string, params: {
    anio: number; tipo: string; desde: number; hasta: number; empleadoId?: string;
    acumulado?: boolean;
  }) {
    const r = await this.client.get<APIResponse<any>>(`/nomina/reportes/${que}`, { params });
    return r.data;
  }

  /** El mismo reporte, en hoja de cálculo. */
  async descargarReporteNominaExcel(que: string, params: {
    anio: number; tipo: string; desde: number; hasta: number; acumulado?: boolean;
  }) {
    const r = await this.client.get(`/nomina/reportes/${que}/excel`, {
      params, responseType: 'blob',
    });
    await this.downloadFile(
      r.data as Blob,
      `${que}-${params.tipo.toLowerCase()}-${params.desde}a${params.hasta}-${params.anio}.xlsx`
    );
  }

  /** El mismo reporte, en PDF (encabezado empresa · reporte · fecha). */
  async descargarReporteNominaPdf(que: string, params: {
    anio: number; tipo: string; desde: number; hasta: number; acumulado?: boolean;
  }) {
    const r = await this.client.get(`/nomina/reportes/${que}/pdf`, {
      params, responseType: 'blob',
    });
    await this.downloadFile(
      r.data as Blob,
      `${que}-${params.tipo.toLowerCase()}-${params.desde}a${params.hasta}-${params.anio}.pdf`
    );
  }

  /** Importa el paquete de un respaldo de NomiPaq (empleados, periodos, recibos). */
  async importarNominaRespaldo(fd: FormData) {
    const r = await this.client.post<APIResponse<any>>('/nomina/importar', fd,
      { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 600000 });
    return r.data;
  }

  /** Descarga la herramienta local de nómina (lee el .bak y deja un .zip para subir). */
  async descargarHerramientaNomina() {
    const r = await this.client.get('/nomina/herramienta', { responseType: 'blob' });
    await this.downloadFile(r.data as Blob, 'Importar respaldo nomina NEXO.zip');
  }

  /** El recibo en PDF, para verlo en pantalla (blob URL). */
  async getReciboPdfBlob(id: string): Promise<string> {
    const r = await this.client.get(`/nomina/recibos/${id}/pdf`, { responseType: 'blob' });
    return URL.createObjectURL(r.data as Blob);
  }

  /** El recibo en PDF, para guardarlo. */
  async descargarReciboPdf(id: string, nombre: string) {
    const r = await this.client.get(`/nomina/recibos/${id}/pdf`, {
      params: { descargar: 'true' }, responseType: 'blob',
    });
    await this.downloadFile(r.data as Blob, nombre);
  }

  /** El mismo concepto a varios trabajadores. ESCRIBE el borrador. */
  async aplicarConceptoAVarios(periodoId: string, body: {
    lado: 'ingresos' | 'egresos'; clave: string;
    /* Las faltas van en `dias`; lo demás en `importe`. El servidor convierte
     * los días a pesos con el salario de cada trabajador. */
    importe?: number; dias?: number;
    empleadoIds: string[]; gravadoManual?: number;
  }) {
    const r = await this.client.post<APIResponse<any>>(
      `/nomina/prenomina/${periodoId}/aplicar-a-varios`, body
    );
    return r.data;
  }

  /** Timbra recibos de nómina ante el PAC. NO se deshace sin cancelar. */
  async timbrarRecibosNomina(ids: string[]) {
    const r = await this.client.post<APIResponse<any>>(
      '/nomina/recibos/timbrar', { ids }, { timeout: 180_000 }
    );
    return r.data;
  }

  /** Deja el finiquito listo en un periodo especial de UNA persona. ESCRIBE. */
  async finiquitoANominaEspecial(empleadoId: string, body: {
    fechaBaja: string; tipo: 'FINIQUITO' | 'LIQUIDACION';
    desde?: string; vacacionesYaDisfrutadas?: number; indemnizacionDias?: number;
    motivo?: string; fechaPago?: string;
  }) {
    const r = await this.client.post<APIResponse<any>>(
      `/nomina/empleados/${empleadoId}/finiquito/a-nomina-especial`, body
    );
    return r.data;
  }

  /**
   * Colonias, municipio y estado de un código postal (catálogo SAT).
   *
   * OJO: este endpoint responde el objeto DIRECTO —{ colonias, estado, … }—
   * y no el envoltorio { success, data } que usa el resto de la API. Es de los
   * de Carta Porte, que nacieron antes de esa convención. Leerlo como si lo
   * llevara devolvía undefined y el combo de colonias salía vacío SIEMPRE,
   * aunque el catálogo estuviera bien: se ve idéntico a "no hay datos".
   *
   * El tipo lo dice para que no se vuelva a leer con `.data`.
   */
  async resolverCodigoPostal(cp: string): Promise<{
    codigoPostal: string;
    colonias: Array<{ clave: string; descripcion: string }>;
    estado: string | null;
    estadoDescripcion: string | null;
    municipios: Array<{ clave: string; descripcion: string }>;
    localidades: Array<{ clave: string; descripcion: string }>;
  }> {
    const r = await this.client.get(`/carta-porte/cp/${cp}`);
    return r.data as any;
  }

  /* ── CFDI de nómina ── */
  async getRecibosNomina(params: { estatus?: string; periodoId?: string } = {}) {
    const r = await this.client.get<APIResponse<any>>('/nomina/recibos', { params });
    return r.data;
  }
  /** CFDI de nómina recuperados del respaldo (histórico timbrado en la bóveda). */
  async getCfdiNominaRecuperados(anio?: number) {
    const r = await this.client.get<APIResponse<any[]>>('/nomina/cfdi-recuperados', { params: anio ? { anio } : {} });
    return r.data;
  }
  /** Años con CFDI de nómina recuperados (para el filtro por ejercicio). */
  async getAniosNominaRecuperados() {
    const r = await this.client.get<APIResponse<number[]>>('/nomina/cfdi-recuperados/anios');
    return r.data;
  }
  async descargarXmlNominaRecuperado(uuid: string, nombre: string) {
    const r = await this.client.get(`/nomina/cfdi-recuperados/${uuid}/xml`, { responseType: 'blob' });
    await this.downloadFile(r.data as Blob, nombre);
  }
  async activarTrabajadorRecuperado(uuid: string) {
    const r = await this.client.post<APIResponse<any>>(`/nomina/cfdi-recuperados/${uuid}/activar`, {});
    return r.data;
  }
  /** Completa los datos VACÍOS del trabajador desde el XML del recibo (migración). */
  async completarTrabajadorRecuperado(uuid: string) {
    const r = await this.client.post<APIResponse<any>>(`/nomina/cfdi-recuperados/${uuid}/completar`, {});
    return r.data;
  }
  async getXmlRecibo(id: string) {
    const r = await this.client.get<APIResponse<any>>(`/nomina/recibos/${id}/xml`);
    return r.data;
  }
  async descargarXmlRecibo(id: string, nombre: string) {
    const r = await this.client.get(`/nomina/recibos/${id}/xml`, {
      params: { descargar: 'true' }, responseType: 'blob',
    });
    await this.downloadFile(r.data as Blob, nombre);
  }
  async marcarEnvioPorCorreo(ids: string[], enviar: boolean) {
    const r = await this.client.put<APIResponse<any>>('/nomina/recibos/envio-por-correo', { ids, enviar });
    return r.data;
  }

  /** Recalcula con lo capturado en la rejilla. NO guarda: sigue siendo cálculo. */
  /** Abre el documento legal de la baja (finiquito | renuncia | liquidacion) en PDF. */
  async verDocumentoBaja(reciboId: string, variante: string) {
    // Mismo arreglo que verOpinionPdf: el token sale del interceptor (sessionStorage),
    // no de localStorage (que salía null → 401).
    const r = await this.client.get(`/nomina/baja/${reciboId}/documento`, { params: { variante }, responseType: 'blob' });
    window.open(URL.createObjectURL(r.data as Blob), '_blank');
  }
  async recalcularPrenomina(periodoId: string, captura: any[]) {
    const r = await this.client.post<APIResponse<any>>(
      `/nomina/prenomina/${periodoId}`, { captura }, { timeout: 120_000 }
    );
    return r.data;
  }
  async getPrenomina(periodoId: string) {
    const r = await this.client.get<APIResponse<any>>(`/nomina/prenomina/${periodoId}`, {
      /* Cincuenta trabajadores se calculan al vuelo cada vez: el tiempo de
       * espera por omisión se queda corto en una plantilla grande. */
      timeout: 120_000,
    });
    return r.data;
  }

  /* ── Préstamos de la empresa y créditos FONACOT ── */
  async getCreditosNomina(params: { empleadoId?: string; origen?: string; incluirCerrados?: boolean } = {}) {
    const r = await this.client.get<APIResponse<any>>('/nomina/creditos', {
      params: {
        empleadoId: params.empleadoId || undefined,
        origen: params.origen || undefined,
        incluirCerrados: params.incluirCerrados || undefined,
      },
    });
    return r.data;
  }
  async getCreditoNomina(id: string) {
    const r = await this.client.get<APIResponse<any>>(`/nomina/creditos/${id}`);
    return r.data;
  }
  async crearCreditoNomina(datos: any) {
    const r = await this.client.post<APIResponse<any>>('/nomina/creditos', datos);
    return r.data;
  }
  async abonarCreditoNomina(id: string, datos: any = {}) {
    const r = await this.client.post<APIResponse<any>>(`/nomina/creditos/${id}/abonar`, datos);
    return r.data;
  }
  async cambiarEstatusCreditoNomina(id: string, estatus: string, motivo?: string) {
    const r = await this.client.put<APIResponse<any>>(`/nomina/creditos/${id}/estatus`, { estatus, motivo });
    return r.data;
  }
  async getEmpleados(params: { buscar?: string; incluirBajas?: boolean } = {}) {
    const r = await this.client.get<APIResponse<any>>('/nomina/empleados', {
      params: { buscar: params.buscar || undefined, incluirBajas: params.incluirBajas || undefined },
    });
    return r.data;
  }
  /** Descarga la plantilla Excel para dar de alta trabajadores. */
  async descargarPlantillaEmpleados() {
    const r = await this.client.get('/nomina/empleados/plantilla-excel', { responseType: 'blob' });
    await this.downloadFile(r.data as Blob, 'Plantilla_alta_trabajadores.xlsx');
  }
  /** Da de alta trabajadores desde la plantilla Excel llena. */
  async importarEmpleadosExcel(fd: FormData) {
    const r = await this.client.post<APIResponse<any>>('/nomina/empleados/importar-excel', fd,
      { headers: { 'Content-Type': 'multipart/form-data' } });
    return r.data;
  }
  async getEmpleadosResumen() {
    const r = await this.client.get<APIResponse<any>>('/nomina/empleados/resumen');
    return r.data;
  }
  async getSiguienteNumEmpleado() {
    const r = await this.client.get<APIResponse<any>>('/nomina/empleados/siguiente-numero');
    return r.data;
  }
  async getEmpleado(id: string) {
    const r = await this.client.get<APIResponse<any>>(`/nomina/empleados/${id}`);
    return r.data;
  }
  async crearEmpleado(datos: any) {
    const r = await this.client.post<APIResponse<any>>('/nomina/empleados', datos);
    return r.data;
  }
  async actualizarEmpleado(id: string, datos: any) {
    const r = await this.client.put<APIResponse<any>>(`/nomina/empleados/${id}`, datos);
    return r.data;
  }
  async getModificacionesSalario(empleadoId: string) {
    const r = await this.client.get<APIResponse<any>>(`/nomina/empleados/${empleadoId}/modificaciones-salario`);
    return r.data;
  }
  async agregarModificacionSalario(empleadoId: string, body: {
    fecha: string; salarioDiario: number; sdi?: number | null; motivo?: string;
  }) {
    const r = await this.client.post<APIResponse<any>>(`/nomina/empleados/${empleadoId}/modificaciones-salario`, body);
    return r.data;
  }
  async getVacaciones(empleadoId: string) {
    const r = await this.client.get<APIResponse<any>>(`/nomina/empleados/${empleadoId}/vacaciones`);
    return r.data;
  }
  async agregarVacacion(empleadoId: string, body: {
    fechaInicio: string; fechaFin: string; dias: number; tipo: string; motivo?: string;
  }) {
    const r = await this.client.post<APIResponse<any>>(`/nomina/empleados/${empleadoId}/vacaciones`, body);
    return r.data;
  }
  async eliminarVacacion(empleadoId: string, vacId: string) {
    const r = await this.client.delete<APIResponse<any>>(`/nomina/empleados/${empleadoId}/vacaciones/${vacId}`);
    return r.data;
  }
  async darDeBajaEmpleado(id: string, fecha_baja: string, motivo?: string) {
    const r = await this.client.post<APIResponse<any>>(`/nomina/empleados/${id}/baja`, { fecha_baja, motivo });
    return r.data;
  }
  async reingresarEmpleado(id: string, fecha_reingreso: string) {
    const r = await this.client.post<APIResponse<any>>(`/nomina/empleados/${id}/reingreso`, { fecha_reingreso });
    return r.data;
  }
  /** Varios recibos de golpe. SÓLO LEE: devuelve lo rescatado de cada quien. */
  async revisarEmpleadosDeNomina(archivos: Array<{ nombre: string; xml: string }>) {
    const r = await this.client.post<APIResponse<any>>(
      '/xml-super-import/nomina/revisar-empleados', { archivos },
      /* Doscientos XML se leen y se cruzan contra la base uno por uno: el tiempo
       * de espera por omisión se queda corto y la pantalla mostraría un error de
       * red sobre una lectura que en realidad iba bien. */
      { timeout: 240_000 }
    );
    return r.data;
  }

  /** Da de alta los expedientes ya confirmados en pantalla. */
  async altaDeEmpleadosEnBloque(expedientes: Array<{ archivo?: string; datos: any }>) {
    const r = await this.client.post<APIResponse<any>>(
      '/xml-super-import/nomina/alta-en-bloque', { expedientes }, { timeout: 240_000 }
    );
    return r.data;
  }

  /**
   * Lector de XML → expediente. Lee y propone; NO da de alta a nadie: el alta
   * va por crearEmpleado(), con lo que la persona confirmó en pantalla.
   */
  async proponerEmpleadoDesdeXml(xml: string) {
    const r = await this.client.post<APIResponse<any>>('/xml-super-import/nomina/proponer-empleado', { xml });
    return r.data;
  }

  /* ───────────── Descarga masiva del SAT ───────────── */
  async getSatCredencial() {
    const r = await this.client.get<APIResponse<any>>('/sat-descarga/credencial');
    return r.data;
  }
  /** La e.firma viaja una sola vez; el servidor la cifra y no la devuelve nunca. */
  async subirSatCredencial(cer: File, key: File, password: string) {
    const fd = new FormData();
    fd.append('cer', cer);
    fd.append('key', key);
    fd.append('password', password);
    const r = await this.client.post<APIResponse<any>>('/sat-descarga/credencial', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return r.data;
  }
  async borrarSatCredencial() {
    const r = await this.client.delete<APIResponse<any>>('/sat-descarga/credencial');
    return r.data;
  }
  async getSatTrabajoDetalle(id: string) {
    const r = await this.client.get<APIResponse<any>>(`/sat-descarga/trabajos/${id}`);
    return r.data;
  }
  async getSatTrabajos() {
    const r = await this.client.get<APIResponse<any>>('/sat-descarga/trabajos');
    return r.data;
  }
  async crearSatTrabajo(data: {
    desde: string; hasta: string;
    /** 'ambos' crea dos trabajos: el SAT pide emitidos y recibidos por separado. */
    direccion: 'recibidos' | 'emitidos' | 'ambos';
    tipo?: 'CFDI' | 'Metadata'; filtros?: any;
  }) {
    const r = await this.client.post<APIResponse<any>>('/sat-descarga/trabajos', data);
    return r.data;
  }
  async avanzarSatDescarga(trabajoId?: string) {
    const r = await this.client.post<APIResponse<any>>('/sat-descarga/avanzar', { trabajoId });
    return r.data;
  }
  async getSatComprobantes(params: {
    anio?: number; mes?: number; direccion?: string; rfc?: string; buscar?: string;
  } = {}) {
    const r = await this.client.get<APIResponse<any>>('/sat-descarga/comprobantes', { params });
    return r.data;
  }
  async getSatResumen(anio?: number, mes?: number) {
    const r = await this.client.get<APIResponse<any>>('/sat-descarga/comprobantes/resumen', {
      params: { anio, mes },
    });
    return r.data;
  }
  /* La vista de los submenús: emitidos/recibidos con contraparte, pagado y CC. */
  async getSatComprobantesVista(params: {
    direccion: 'emitidos' | 'recibidos'; anio?: number; mes?: number; buscar?: string;
  }) {
    const r = await this.client.get<APIResponse<any>>('/sat-descarga/comprobantes/vista', { params });
    return r.data;
  }
  /* Detalle de un comprobante: XML+pagos (emitidos) o ficha de metadatos (recibidos). */
  async getSatComprobante(id: string) {
    const r = await this.client.get<APIResponse<any>>(`/sat-descarga/comprobantes/${id}`);
    return r.data;
  }
  /* Asigna (o limpia con null) la cuenta contable —columna CC— de un comprobante. */
  async setSatCuentaContable(id: string, cuenta: string | null) {
    const r = await this.client.put<APIResponse<any>>(`/sat-descarga/comprobantes/${id}/cuenta`, { cuenta });
    return r.data;
  }

  /* ───────────── Mensajería interna ───────────── */
  async getMensajes(buzon: 'recibidos' | 'enviados' = 'recibidos') {
    const r = await this.client.get<APIResponse<any>>('/mensajes', { params: { buzon } });
    return r.data;
  }
  async getMensajesNoLeidos() {
    const r = await this.client.get<APIResponse<any>>('/mensajes/no-leidos');
    return r.data;
  }
  async getDestinatarios() {
    const r = await this.client.get<APIResponse<any>>('/mensajes/destinatarios');
    return r.data;
  }
  async enviarMensaje(data: { paraUserId: string; asunto?: string; cuerpo: string; respondeA?: string }) {
    const r = await this.client.post<APIResponse<any>>('/mensajes', data);
    return r.data;
  }
  async marcarMensajeLeido(id: string) {
    const r = await this.client.post<APIResponse<any>>(`/mensajes/${id}/leido`);
    return r.data;
  }
  async marcarTodosLosMensajes() {
    const r = await this.client.post<APIResponse<any>>('/mensajes/leer-todo');
    return r.data;
  }

  /* ───────────── Presencia: quién más tiene abierta la pantalla ───────────── */
  async presenciaEntrar(recurso: string, recursoId: string) {
    const r = await this.client.post<APIResponse<any>>('/presencia/entrar', { recurso, recursoId });
    return r.data;
  }
  async presenciaSalir(recurso: string, recursoId: string) {
    const r = await this.client.post<APIResponse<any>>('/presencia/salir', { recurso, recursoId });
    return r.data;
  }

  /* ───────────── Auditoría: qué dice el SAT de nuestros CFDI ───────────── */
  async getAuditoria(params?: { soloDiscrepancias?: boolean; estado?: string; docType?: string }) {
    const r = await this.client.get<APIResponse<any>>('/auditoria', { params });
    return r.data;
  }
  async getAuditoriaResumen() {
    const r = await this.client.get<APIResponse<any>>('/auditoria/resumen');
    return r.data;
  }
  /** `todos = true` vuelve a preguntar por el universo completo, sin esperar las 72 h. */
  async correrAuditoria(todos = false) {
    const r = await this.client.post<APIResponse<any>>('/auditoria/revisar', { todos });
    return r.data;
  }
  async revisarComprobanteSat(docType: string, docId: string) {
    const r = await this.client.post<APIResponse<any>>(`/auditoria/revisar/${docType}/${docId}`);
    return r.data;
  }

  /* ───────────── Remesas de pago (la lista del viernes) ───────────── */
  async getPaymentRuns(params?: { from?: string; to?: string; status?: string }) {
    const r = await this.client.get<APIResponse<any>>('/treasury/payment-runs', { params });
    return r.data;
  }
  async getPaymentRun(id: string) {
    const r = await this.client.get<APIResponse<any>>(`/treasury/payment-runs/${id}`);
    return r.data;
  }
  async createPaymentRun(data: { paymentDate: string; notes?: string; paymentIds: string[] }) {
    const r = await this.client.post<APIResponse<any>>('/treasury/payment-runs', data);
    return r.data;
  }
  async addPaymentsToRun(id: string, paymentIds: string[]) {
    const r = await this.client.post<APIResponse<any>>(
      `/treasury/payment-runs/${id}/payments`, { paymentIds });
    return r.data;
  }
  async removePaymentFromRun(id: string, paymentId: string) {
    const r = await this.client.delete<APIResponse<any>>(
      `/treasury/payment-runs/${id}/payments/${paymentId}`);
    return r.data;
  }
  async setPaymentRunStatus(id: string, status: 'AUTHORIZED' | 'PAID' | 'CANCELLED' | 'DRAFT') {
    const r = await this.client.put<APIResponse<any>>(
      `/treasury/payment-runs/${id}/status`, { status });
    return r.data;
  }
  async getTreasurySummary() {
    const r = await this.client.get<APIResponse<any>>('/treasury/summary');
    return r.data;
  }
  async createTreasuryPayment(data: { supplierId: string; amount: number; dueDate: string; notes?: string }) {
    const r = await this.client.post<APIResponse<any>>('/treasury/payments', data);
    return r.data;
  }
  async payTreasuryPayment(id: string, data?: { paidAt?: string; notes?: string }) {
    const r = await this.client.post<APIResponse<any>>(`/treasury/payments/${id}/pay`, data || {});
    return r.data;
  }
  async rescheduleTreasuryPayment(id: string, dueDate: string) {
    const r = await this.client.put<APIResponse<any>>(`/treasury/payments/${id}/reschedule`, { dueDate });
    return r.data;
  }
  async cancelTreasuryPayment(id: string, motivo?: string) {
    const r = await this.client.post<APIResponse<any>>(`/treasury/payments/${id}/cancel`, { motivo });
    return r.data;
  }

  /* ───────────── Equipo / capacidades (Fase 8 ALMACEN §8) ───────────── */
  async getTeamCapabilities() {
    const r = await this.client.get<APIResponse<any>>('/team/capabilities');
    return r.data;
  }
  async getTeamUsers() {
    const r = await this.client.get<APIResponse<any>>('/team/users');
    return r.data;
  }
  async setUserCapabilities(userId: string, capabilities: string[]) {
    const r = await this.client.put<APIResponse<any>>(`/team/users/${userId}/capabilities`, { capabilities });
    return r.data;
  }
  async createTeamUser(data: { email: string; password: string; firstName: string; lastName?: string; role: string; workGroup: string; }) {
    const r = await this.client.post<APIResponse<any>>('/team/users', data);
    return r.data;
  }
  async updateTeamUser(userId: string, data: any) {
    const r = await this.client.patch<APIResponse<any>>(`/team/users/${userId}`, data);
    return r.data;
  }
  async deleteTeamUser(userId: string) {
    const r = await this.client.delete<APIResponse<any>>(`/team/users/${userId}`);
    return r.data;
  }

  /* ───────────── Inventario físico (Fase 6 ALMACEN §11) ───────────── */
  async getPhysicalCounts() {
    const r = await this.client.get<APIResponse<any>>('/physical-counts');
    return r.data;
  }
  async getPhysicalCount(id: string) {
    const r = await this.client.get<APIResponse<any>>(`/physical-counts/${id}`);
    return r.data;
  }
  async openPhysicalCount(data: { warehouseId: string; category?: string; notes?: string }) {
    const r = await this.client.post<APIResponse<any>>('/physical-counts', data);
    return r.data;
  }
  async capturePhysicalCount(id: string, items: Array<{ itemId: string; countedQty: number }>) {
    const r = await this.client.put<APIResponse<any>>(`/physical-counts/${id}/capture`, { items });
    return r.data;
  }
  async closePhysicalCount(id: string) {
    const r = await this.client.post<APIResponse<any>>(`/physical-counts/${id}/close`);
    return r.data;
  }
  async cancelPhysicalCount(id: string) {
    const r = await this.client.post<APIResponse<any>>(`/physical-counts/${id}/cancel`);
    return r.data;
  }

  /* ───────────── Proveedores (read-only) ───────────── */
  async listSuppliers(params: { search?: string; limit?: number } = {}) {
    const r = await this.client.get('/suppliers', { params });
    return r.data;
  }
  async getSupplier(id: string) {
    const r = await this.client.get(`/suppliers/${id}`);
    return r.data;
  }
  async getSupplierBanks() {
    const r = await this.client.get('/suppliers/banks');
    return r.data;
  }
  async createSupplier(data: any) {
    const r = await this.client.post('/suppliers', data);
    return r.data;
  }
  async updateSupplier(id: string, data: any) {
    const r = await this.client.put(`/suppliers/${id}`, data);
    return r.data;
  }

  /* ── Empresas de un usuario (multi-empresa) ─────────────────────────────
   * Las tres devuelven la lista completa ya actualizada, para que la pantalla
   * no la reconstruya en memoria y muestre algo distinto de lo guardado. */
  async empresasDeUsuario(userId: string) {
    const r = await this.client.get<APIResponse<Array<{
      id: string; rfc: string; business_name: string;
      work_group: string | null; is_default: boolean;
    }>>>(`/admin/users/${userId}/companies`);
    return r.data;
  }

  async asociarEmpresaAUsuario(userId: string, companyId: string, workGroup?: string) {
    const r = await this.client.post<APIResponse<any>>(
      `/admin/users/${userId}/companies`, { companyId, workGroup });
    return r.data;
  }

  async desasociarEmpresaDeUsuario(userId: string, companyId: string) {
    const r = await this.client.delete<APIResponse<any>>(
      `/admin/users/${userId}/companies/${companyId}`);
    return r.data;
  }

  async adminListCompanies(params: { search?: string } = {}) {
    const r = await this.client.get('/admin/companies', { params });
    return r.data;
  }
  /** Empresas que pueden EMITIR el cobro del servicio (selector "empresa que factura"). */
  async adminListEmisores() {
    const r = await this.client.get('/admin/companies/emisores');
    return r.data;
  }

  /* ── Facturación por usuario (SUPER_ADMIN) ── */
  async getFacturacionConfig() {
    return (await this.client.get('/admin/facturacion/config')).data;
  }
  async setFacturacionConfig(d: { precioUsuario?: number; timbresIncluidos?: number; timbreExtra?: number; timbresPorUsuario?: number; timbreExtraPorUsuario?: number }) {
    return (await this.client.put('/admin/facturacion/config', d)).data;
  }
  async generarFacturacion(periodo?: string) {
    return (await this.client.post('/admin/facturacion/generar', { periodo })).data;
  }
  async getFacturacionLista(periodo?: string) {
    return (await this.client.get('/admin/facturacion/lista', { params: { periodo } })).data;
  }
  async getFacturacionConsolidado(periodo?: string) {
    return (await this.client.get('/admin/facturacion/consolidado', { params: { periodo } })).data;
  }
  async pagarFacturacion(id: string) {
    return (await this.client.post(`/admin/facturacion/${id}/pagar`)).data;
  }
  async suspenderFacturacion(id: string) {
    return (await this.client.post(`/admin/facturacion/${id}/suspender`)).data;
  }
  async reactivarFacturacion(id: string) {
    return (await this.client.post(`/admin/facturacion/${id}/reactivar`)).data;
  }
  /** Emite el CFDI del cargo mensual desde la emisora elegida (GHC/SAJ). */
  async emitirFacturaMensual(id: string, emisoraId: string) {
    return (await this.client.post(`/admin/facturacion/${id}/emitir`, { emisoraId })).data;
  }

  /* ── Emisor de facturas (SUPER_ADMIN): empresas emisoras, SMTP y depósito ── */
  async getEmisores() {
    return (await this.client.get('/admin/emisores')).data;
  }
  async setEmisor(companyId: string, d: { esEmisor: boolean; predeterminado: boolean }) {
    return (await this.client.post(`/admin/emisores/${companyId}`, d)).data;
  }
  async setEmisorSmtp(companyId: string, d: { mail_host: string; mail_port?: number; mail_secure?: boolean; mail_user: string; mail_pass?: string; mail_from?: string }) {
    return (await this.client.put(`/admin/emisores/${companyId}/smtp`, d)).data;
  }
  async testEmisorSmtp(companyId: string, to?: string) {
    return (await this.client.post(`/admin/emisores/${companyId}/smtp/test`, { to })).data;
  }
  async setEmisorDeposito(companyId: string, d: { deposito_banco?: string; deposito_titular?: string; deposito_clabe?: string; deposito_cuenta?: string; deposito_referencia?: string; deposito_notas?: string }) {
    return (await this.client.put(`/admin/emisores/${companyId}/deposito`, d)).data;
  }
  // ── SatGo (proveedor fiscal) ──
  async getSatgoConfig() {
    return (await this.client.get('/admin/satgo/config')).data;
  }
  async setSatgoConfig(d: { baseUrl?: string; ambiente?: string }) {
    return (await this.client.put('/admin/satgo/config', d)).data;
  }
  async bootstrapSatgo(portalToken: string) {
    return (await this.client.post('/admin/satgo/bootstrap', { portalToken })).data;
  }
  /** Pega DIRECTO la API Key permanente de SatGo (la misma que ya sirve en otra app). */
  async setSatgoApiKey(apiKey: string) {
    return (await this.client.post('/admin/satgo/apikey', { apiKey })).data;
  }
  /** Prueba la conexión con SatGo (Auth/token). Avisa si la API Key caducó. */
  async probarSatgo() {
    return (await this.client.get('/admin/satgo/probar')).data;
  }
  /** Tablero de conectores externos: SAT (SatGo), Banco de México e INEGI. */
  async getConectoresEstado() {
    return (await this.client.get('/admin/satgo/conectores')).data;
  }
  /** Panel de rendimiento del servicio en Render: memoria del proceso/contenedor
   *  y disco ocupado por la base de datos (SUPER_ADMIN). */
  async getRendimiento() {
    return (await this.client.get<APIResponse<any>>('/admin/rendimiento')).data;
  }
  async adminCreateCompany(data: { rfc: string; businessName: string; fiscalRegime: string;
                                   postalCode?: string; billingPlan?: string; capTimbres?: number;
                                   monthlyFee?: number; extraStampFee?: number;
                                   stampPackageCode?: string;
                                   street?: string; extNumber?: string; neighborhood?: string;
                                   city?: string; municipality?: string; state?: string;
                                   empresaCobroId?: string }) {
    const r = await this.client.post('/admin/companies', data);
    return r.data;
  }
  async adminUpdateCompany(id: string, data: any) {
    const r = await this.client.put(`/admin/companies/${id}`, data);
    return r.data;
  }
  /** Asigna una emisora de cobro (despacho) a varias empresas de un tirón (agrupar). */
  async asignarEmisoraEmpresas(companyIds: string[], emisoraId: string | null) {
    const r = await this.client.post<APIResponse<{ asignadas: number }>>('/admin/companies/asignar-emisora', { companyIds, emisoraId });
    return r.data;
  }

  /* ── Despachos contables (SUPER_ADMIN): alta/baja y PRECIOS por despacho ── */
  async listDespachos() {
    return (await this.client.get('/admin/despachos')).data;
  }
  async crearDespacho(d: { nombre: string; precioUsuario?: number | null; timbresIncluidos?: number | null;
                           timbreExtra?: number | null; timbresPorUsuario?: number | null;
                           timbreExtraPorUsuario?: number | null; emisoraPredeterminadaId?: string | null; notas?: string }) {
    return (await this.client.post('/admin/despachos', d)).data;
  }
  async actualizarDespacho(id: string, d: any) {
    return (await this.client.put(`/admin/despachos/${id}`, d)).data;
  }
  async eliminarDespacho(id: string) {
    return (await this.client.delete(`/admin/despachos/${id}`)).data;
  }
  /** Mete varias empresas al despacho de un tirón. */
  async asignarDespachoEmpresas(despachoId: string, companyIds: string[]) {
    return (await this.client.post(`/admin/despachos/${despachoId}/asignar`, { companyIds })).data;
  }
  /** Activa el cumplimiento (SAT/CSF/IMSS) en automático para TODAS las empresas,
   *  para que el barrido de los lunes 5:30 las incluya. */
  async adminActivarCumplimientoTodas() {
    const r = await this.client.post<APIResponse<{ empresas: number; configs: number }>>('/admin/companies/cumplimiento/activar-todas');
    return r.data;
  }
  /**
   * Lee el .cer (y opcionalmente comprueba el .key) SIN guardar nada.
   * Devuelve No. de certificado, vigencia, RFC y razón social para autollenar
   * el formulario: teclear 20 dígitos a mano es la forma más cara de romper
   * un timbrado.
   */
  /* ── Permisos de un usuario desde el panel de plataforma ──
   * Grupo de trabajo (qué pantallas ve) + capacidades finas (qué puede hacer).
   * Es el equivalente de /team para el SUPER_ADMIN, que no tiene empresa y por
   * eso no puede usar aquellas rutas. */
  async adminGetUserPermissions(id: string) {
    const r = await this.client.get<APIResponse<any>>(`/admin/users/${id}/permissions`);
    return r.data;
  }
  async adminSetUserPermissions(id: string, data: { workGroup?: string; capabilities?: string[]; extraModules?: string[] }) {
    const r = await this.client.put<APIResponse<any>>(`/admin/users/${id}/permissions`, data);
    return r.data;
  }
  async adminInspectCSD(id: string, data: { cerBase64: string; keyBase64?: string; keyPassword?: string }) {
    const r = await this.client.post<APIResponse<{
      no_certificado: string; rfc: string | null; razon_social: string | null;
      valid_from: string; valid_to: string;
      rfc_matches: boolean; company_rfc: string;
      expired: boolean; not_yet_valid: boolean;
      key_matches: boolean | null; key_error: string | null;
    }>>(`/admin/companies/${id}/csd/inspect`, data);
    return r.data;
  }
  async adminUploadCSD(id: string, data: { noCertificado: string; cerBase64: string;
                                            keyBase64: string; keyPassword: string;
                                            validFrom?: string; validTo?: string }) {
    const r = await this.client.post(`/admin/companies/${id}/csd`, data);
    return r.data;
  }
  async adminDeleteCSD(id: string) {
    const r = await this.client.delete(`/admin/companies/${id}/csd`);
    return r.data;
  }
  /**
   * Preview server-side de lo que se borraría (no ejecuta). Requiere el RFC
   * exacto — el backend valida y devuelve un 400 si no coincide.
   */
  async adminFullDeleteCompanyDryRun(id: string, confirmRfc: string) {
    const r = await this.client.delete(`/admin/companies/${id}/full-delete`, {
      data: { confirmRfc, dryRun: true },
    });
    return r.data;
  }
  /**
   * Borrado TOTAL — requiere confirmRfc + confirmText="ELIMINAR" en el body.
   * Doble validación server-side. No hay rollback.
   */
  async adminFullDeleteCompany(id: string, body: { confirmRfc: string; confirmText: string }) {
    const r = await this.client.delete(`/admin/companies/${id}/full-delete`, { data: body });
    return r.data;
  }

  /* ────── Facturación y consumo (SUPER_ADMIN) ────── */

  async adminBillingCurrentMonth() {
    const r = await this.client.get('/admin/billing/current-month');
    return r.data;
  }
  async adminBillingHistory(year: number) {
    const r = await this.client.get('/admin/billing/history', { params: { year } });
    return r.data;
  }
  async adminBillingCompanyHistory(companyId: string) {
    const r = await this.client.get(`/admin/billing/company/${companyId}/history`);
    return r.data;
  }
  async adminBillingCloseMonth(period?: string) {
    const r = await this.client.post('/admin/billing/close-month', period ? { period } : {});
    return r.data;
  }
  async adminBillingMarkPaid(invoicingId: string) {
    const r = await this.client.patch(`/admin/billing/${invoicingId}/mark-paid`);
    return r.data;
  }
  async adminBillingIssueInvoice(invoicingId: string) {
    const r = await this.client.post(`/admin/billing/${invoicingId}/issue-invoice`);
    return r.data;
  }

  /* ────── Prepago FLEX (SUPER_ADMIN) ────── */

  async adminPrepaidBalances() {
    const r = await this.client.get('/admin/prepaid/balances');
    return r.data;
  }
  async adminPrepaidPurchases(companyId: string) {
    const r = await this.client.get(`/admin/prepaid/${companyId}/purchases`);
    return r.data;
  }
  async adminPrepaidRecharge(companyId: string, body: {
    stampsBought: number;
    unitPriceMxn?: number;
    paymentMethod?: string;
    paymentReference?: string;
    notes?: string;
  }) {
    const r = await this.client.post(`/admin/prepaid/${companyId}/recharge`, body);
    return r.data;
  }
  async adminPrepaidSetThreshold(companyId: string, threshold: number) {
    const r = await this.client.patch(`/admin/prepaid/${companyId}/threshold`, { threshold });
    return r.data;
  }

  /* ────── Manifiesto PAC (firma con e.firma) ────── */

  async getManifestStatus() {
    const r = await this.client.get('/manifest');
    return r.data;
  }
  async getManifestText() {
    const r = await this.client.get('/manifest/text');
    return r.data;
  }
  async signManifest(body: { cerB64: string; keyB64: string; password: string }) {
    const r = await this.client.post('/manifest/sign', body);
    return r.data;
  }
  async manifestPdf() {
    const r = await this.client.get('/manifest/pdf', { responseType: 'blob' });
    return r.data as Blob;
  }
  async adminCompanyUsage(id: string) {
    const r = await this.client.get(`/admin/companies/${id}/usage`);
    return r.data;
  }
  async adminAuditLog(params: { userId?: string; action?: string; limit?: number } = {}) {
    const r = await this.client.get('/admin/audit', { params });
    return r.data;
  }

  /* ─── Tipos de cambio (Banxico) ─── */
  async getExchangeRate(moneda: string, fecha?: string) {
    const r = await this.client.get<{
      moneda: string; valor: number; fecha: string;
      fechaDeterminacion?: string; fuente: string; vigente: boolean;
    }>(`/exchange-rates/${moneda}`, { params: fecha ? { fecha } : {} });
    return r.data;
  }
  async getExchangeRates(fecha?: string) {
    const r = await this.client.get<{ items: any[] }>('/exchange-rates', {
      params: fecha ? { fecha } : {},
    });
    return r.data;
  }
  async getExchangeRateHistory(moneda: string, limit = 60) {
    const r = await this.client.get<{ items: any[] }>(`/exchange-rates/${moneda}/history`, {
      params: { limit },
    });
    return r.data;
  }
  async getExchangeRateLog(limit = 50) {
    const r = await this.client.get<{ items: any[] }>('/exchange-rates/log', { params: { limit } });
    return r.data;
  }
  /** Fuerza la consulta a Banxico. Sin `moneda` actualiza USD, EUR y GBP. */
  async updateExchangeRates(moneda?: string) {
    const r = await this.client.post<{ actualizadas: any[]; fallidas: any[] }>(
      '/exchange-rates/update', moneda ? { moneda } : {},
    );
    return r.data;
  }
  /** Captura manual: la red de seguridad cuando Banxico no responde. */
  async setExchangeRateManual(moneda: string, fecha: string, valor: number) {
    const r = await this.client.post('/exchange-rates/manual', { moneda, fecha, valor });
    return r.data;
  }

  /* ─── Diferencia cambiaria ─── */
  async getFxDifference(params: { desde?: string; hasta?: string; moneda?: string } = {}) {
    const r = await this.client.get<{
      periodo: any; detalle: any[]; porMoneda: any[];
      global: { diferencia: number; utilidad: number; perdida: number };
    }>('/fx-difference', { params });
    return r.data;
  }
  async getFxDifferenceByInvoice(invoiceId: string) {
    const r = await this.client.get<{ items: any[]; diferenciaAcumulada: number }>(
      `/fx-difference/por-factura/${invoiceId}`,
    );
    return r.data;
  }

  /* ─── Carta Porte 3.1 ─── */
  async searchCartaPorteCatalog(name: string, q: string, limit = 50, extra: Record<string, string> = {}) {
    const res = await this.client.get<{ items: Array<{ clave: string; descripcion: string; [k: string]: any }> }>(
      `/carta-porte/catalogs/${name}`,
      { params: { q, limit, ...extra } },
    );
    return res.data;
  }
  /** Cruces fronterizos México–EUA: ayuda de captura, no catálogo del SAT. */
  async getCPCrucesFronterizos() {
    const r = await this.client.get<{ items: Array<{
      clave: string; nombreMx: string; estadoMx: string; nombreUs: string; estadoUs: string;
    }> }>('/carta-porte/cruces-fronterizos');
    return r.data;
  }
  /**
   * Por dónde entra o sale la mercancía, según el medio: cruce carretero
   * (01 y 04), puerto (02) o aeropuerto (03).
   */
  async getCPPuntosEntradaSalida(medio: string, q = '') {
    const r = await this.client.get<{
      tipo: 'cruce' | 'puerto' | 'aeropuerto';
      items: Array<{ clave: string; descripcion: string }>;
    }>('/carta-porte/puntos-entrada-salida', { params: { medio, q } });
    return r.data;
  }
  /** Estaciones para NumEstacion/NombreEstacion de una ubicación (02, 03, 04). */
  async searchCPEstaciones(medio: string, q = '', limit = 50) {
    const r = await this.client.get<{ items: Array<{ clave: string; descripcion: string }> }>(
      '/carta-porte/estaciones', { params: { medio, q, limit } },
    );
    return r.data;
  }
  /* ─── Super lector XML (CFDI + CP + Nómina + Pagos + NC) ─── */
  async xmlSuperDetect(xml: string) {
    const r = await this.client.post<{ detection: any; duplicates: Record<string, { exists: boolean; id?: string }> }>('/xml-super-import/detect', { xml });
    return r.data;
  }
  async xmlSuperApply(payload: any) {
    const r = await this.client.post<any>('/xml-super-import/apply', payload);
    return r.data;
  }
  async xmlSuperCheckExisting(payload: any) {
    const r = await this.client.post<any>('/xml-super-import/check-existing', payload);
    return r.data;
  }
  async xmlSuperApplySelected(payload: any) {
    const r = await this.client.post<any>('/xml-super-import/apply-selected', payload);
    return r.data;
  }

  /* ─── Contrato de servicio con e.firma ─── */
  async getContract() {
    const r = await this.client.get('/contract');
    return r.data;
  }
  async signContract(body: { cerB64: string; keyB64: string; password: string }) {
    const r = await this.client.post('/contract/sign', body);
    return r.data;
  }
  async verifyContract() {
    const r = await this.client.get('/contract/verify');
    return r.data;
  }

  /* ─── Carta Porte: Resolver CP → colonias ─── */
  async resolveCP(cp: string) {
    const res = await this.client.get<{
      codigoPostal: string;
      colonias: Array<{ clave: string; descripcion: string; codigo_postal: string }>;
      estado: string | null;
      estadoDescripcion: string | null;
      municipios: Array<{ clave: string; descripcion: string }>;
      localidades: Array<{ clave: string; descripcion: string }>;
    }>(`/carta-porte/cp/${cp}`);
    return res.data;
  }

  /**
   * Domicilio extranjero: el código postal dice el ESTADO, nunca la ciudad.
   * Responde igual cuando no lo sabe — con `estado: null` y el motivo.
   */
  async resolveCPInternacional(pais: string, codigo: string) {
    const res = await this.client.get<{
      pais: string;
      codigoPostal: string;
      estado: string | null;
      estadoDescripcion: string | null;
      ciudad: null;
      mensaje: string;
    }>(`/carta-porte/cp-internacional/${pais}/${encodeURIComponent(codigo)}`);
    return res.data;
  }

  /* ─── Mercancías transportadas ─── */
  async listMercanciasCatalog(params?: { search?: string; clienteRfc?: string }) {
    const q = new URLSearchParams();
    if (params?.search) q.set('search', params.search);
    if (params?.clienteRfc) q.set('clienteRfc', params.clienteRfc);
    const r = await this.client.get<{ items: any[] }>(`/carta-porte/mercancias?${q}`);
    return r.data;
  }
  async listMercanciasBitacora(params?: { invoiceId?: string; from?: string; to?: string }) {
    const q = new URLSearchParams();
    if (params?.invoiceId) q.set('invoiceId', params.invoiceId);
    if (params?.from) q.set('from', params.from);
    if (params?.to) q.set('to', params.to);
    const r = await this.client.get<{ items: any[] }>(`/carta-porte/mercancias/bitacora?${q}`);
    return r.data;
  }
  async deleteMercanciaCatalog(id: string) {
    await this.client.delete(`/carta-porte/mercancias/${id}`);
  }

  /* ─── Carta Porte: Lugares frecuentes ─── */
  async listCPLugares(q?: string, tipo?: string) {
    const res = await this.client.get<{ items: any[] }>('/carta-porte/lugares', { params: { q, tipo } });
    return res.data.items || [];
  }
  async createCPLugar(data: any) {
    const res = await this.client.post<any>('/carta-porte/lugares', data);
    return res.data;
  }
  async updateCPLugar(id: string, data: any) {
    const res = await this.client.patch<any>(`/carta-porte/lugares/${id}`, data);
    return res.data;
  }
  async deleteCPLugar(id: string) {
    const res = await this.client.delete<{ removed: number }>(`/carta-porte/lugares/${id}`);
    return res.data;
  }

  /* ─── CP: Importador XML ─── */
  async cpImportPreview(xml: string) {
    const r = await this.client.post<any>('/carta-porte/importar-xml/preview', { xml });
    return r.data;
  }
  async cpImportApply(payload: any) {
    const r = await this.client.post<any>('/carta-porte/importar-xml/apply', payload);
    return r.data;
  }

  /* ─── CP: Vehículos ─── */
  async listCPVehiculos(q?: string) {
    const r = await this.client.get<{ items: any[] }>('/carta-porte/vehiculos', { params: { q } });
    return r.data.items || [];
  }
  async createCPVehiculo(data: any) { return (await this.client.post<any>('/carta-porte/vehiculos', data)).data; }
  async updateCPVehiculo(id: string, data: any) { return (await this.client.patch<any>(`/carta-porte/vehiculos/${id}`, data)).data; }
  async deleteCPVehiculo(id: string) { return (await this.client.delete<any>(`/carta-porte/vehiculos/${id}`)).data; }

  /* ─── CP: Aseguradoras ─── */
  async listCPAseguradoras(q?: string, tipo?: string) {
    const r = await this.client.get<{ items: any[] }>('/carta-porte/aseguradoras', { params: { q, tipo } });
    return r.data.items || [];
  }
  async createCPAseguradora(data: any) { return (await this.client.post<any>('/carta-porte/aseguradoras', data)).data; }
  async updateCPAseguradora(id: string, data: any) { return (await this.client.patch<any>(`/carta-porte/aseguradoras/${id}`, data)).data; }
  async deleteCPAseguradora(id: string) { return (await this.client.delete<any>(`/carta-porte/aseguradoras/${id}`)).data; }

  /* ─── CP: Operadores ─── */
  async listCPOperadores(q?: string, tipo?: string) {
    const r = await this.client.get<{ items: any[] }>('/carta-porte/operadores', { params: { q, tipo } });
    return r.data.items || [];
  }
  async createCPOperador(data: any) { return (await this.client.post<any>('/carta-porte/operadores', data)).data; }
  async updateCPOperador(id: string, data: any) { return (await this.client.patch<any>(`/carta-porte/operadores/${id}`, data)).data; }
  async deleteCPOperador(id: string) { return (await this.client.delete<any>(`/carta-porte/operadores/${id}`)).data; }

  async listCartaPorte() {
    const res = await this.client.get<{ items: any[] }>(`/carta-porte/list`);
    return res.data;
  }
  async getCartaPorte(invoiceId: string) {
    const res = await this.client.get<{ cartaPorte: any }>(`/invoices/${invoiceId}/carta-porte`);
    return res.data.cartaPorte;
  }
  async saveCartaPorte(invoiceId: string, data: any) {
    const res = await this.client.put<{ id: number }>(`/invoices/${invoiceId}/carta-porte`, data);
    return res.data;
  }
  async deleteCartaPorte(invoiceId: string) {
    const res = await this.client.delete<{ removed: number }>(`/invoices/${invoiceId}/carta-porte`);
    return res.data;
  }

  /**
   * Utility methods
   */
  async downloadFile(blob: Blob, filename: string) {
    // Aseguramos un tipo MIME válido — algunos navegadores ignoran download
    // si el Blob viene sin type (lo tratan como "navigate" en el mismo tab).
    const fixed = blob.type
      ? blob
      : new Blob([blob], {
          type: filename.endsWith('.xml')
            ? 'application/xml'
            : filename.endsWith('.pdf')
              ? 'application/pdf'
              : 'application/octet-stream',
        });
    const url = window.URL.createObjectURL(fixed);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // Liberamos el ObjectURL tras un breve delay para no abortar la descarga
    setTimeout(() => window.URL.revokeObjectURL(url), 4000);
  }

  /* ─── Checador (control de asistencia) ─── */
  async getCheckadorConfig() {
    const r = await this.client.get<APIResponse<any>>('/checador/config');
    return r.data;
  }
  async setCheckadorConfig(body: any) {
    const r = await this.client.put<APIResponse<any>>('/checador/config', body);
    return r.data;
  }
  async getCheckadorTurnos() {
    const r = await this.client.get<APIResponse<any[]>>('/checador/turnos');
    return r.data;
  }
  async crearCheckadorTurno(body: any) {
    const r = await this.client.post<APIResponse<any>>('/checador/turnos', body);
    return r.data;
  }
  async actualizarCheckadorTurno(id: string, body: any) {
    const r = await this.client.put<APIResponse<any>>(`/checador/turnos/${id}`, body);
    return r.data;
  }
  async borrarCheckadorTurno(id: string) {
    const r = await this.client.delete<APIResponse<any>>(`/checador/turnos/${id}`);
    return r.data;
  }
  /* Kioscos: ubicación fija por centro de trabajo. */
  async getCheckadorKioscos() {
    const r = await this.client.get<APIResponse<any[]>>('/checador/kioscos');
    return r.data;
  }
  async crearCheckadorKiosco(body: any) {
    const r = await this.client.post<APIResponse<any>>('/checador/kioscos', body);
    return r.data;
  }
  async actualizarCheckadorKiosco(id: string, body: any) {
    const r = await this.client.put<APIResponse<any>>(`/checador/kioscos/${id}`, body);
    return r.data;
  }
  async borrarCheckadorKiosco(id: string) {
    const r = await this.client.delete<APIResponse<any>>(`/checador/kioscos/${id}`);
    return r.data;
  }
  async getCheckadorHorario(empleadoId: string) {
    const r = await this.client.get<APIResponse<any>>(`/checador/empleados/${empleadoId}/horario`);
    return r.data;
  }
  async setCheckadorHorario(empleadoId: string, body: any) {
    const r = await this.client.put<APIResponse<any>>(`/checador/empleados/${empleadoId}/horario`, body);
    return r.data;
  }
  /** Asigna el mismo turno (o EXENTO) a varios empleados de un golpe. */
  async asignarHorarioMasivo(empleadoIds: string[], tipo: string, turnoId?: string | null) {
    const r = await this.client.put<APIResponse<any>>('/checador/horarios/masivo',
      { empleadoIds, tipo, turno_id: turnoId ?? null });
    return r.data;
  }
  async getCheckadorConsentimiento(empleadoId: string) {
    const r = await this.client.get<APIResponse<any>>(`/checador/empleados/${empleadoId}/consentimiento`);
    return r.data;
  }
  async setCheckadorConsentimiento(empleadoId: string, body: any) {
    const r = await this.client.put<APIResponse<any>>(`/checador/empleados/${empleadoId}/consentimiento`, body);
    return r.data;
  }
  async getCheckadorEnrolamiento(empleadoId: string) {
    const r = await this.client.get<APIResponse<any>>(`/checador/empleados/${empleadoId}/enrolamiento`);
    return r.data;
  }
  /* ── Kiosco / enrolamiento facial (PWA) ── */
  async checadorEmpleadosEnrolar() {
    const r = await this.client.get<APIResponse<any[]>>('/checador/empleados-enrolar');
    return r.data;
  }
  async checadorEnrolar(empleadoId: string, descriptores: number[][]) {
    const r = await this.client.post<APIResponse<any>>(`/checador/empleados/${empleadoId}/enrolar`, { descriptores });
    return r.data;
  }
  async checadorChecada(payload: { descriptor: number[]; lat?: number | null; lng?: number | null; origen?: string; device?: any; kioscoId?: string | null }) {
    const r = await this.client.post<APIResponse<any>>('/checador/checada', payload);
    return r.data;
  }
  /** Registro del día: por trabajador, entrada/salida (hora de México). */
  async checadorAsistenciaDia(fecha?: string) {
    const r = await this.client.get<APIResponse<any>>('/checador/asistencia/dia', { params: fecha ? { fecha } : {} });
    return r.data;
  }
  /** Historial (registro electrónico legal) de checadas, filtrable. */
  async checadorHistorial(f: { desde?: string; hasta?: string; empleadoId?: string; limit?: number } = {}) {
    const r = await this.client.get<APIResponse<any[]>>('/checador/asistencia/historial', { params: f });
    return r.data;
  }
  /** Descarga el historial en Excel o PDF (con token; dispara la descarga). */
  async descargarHistorialChecador(f: { desde?: string; hasta?: string; empleadoId?: string }, formato: 'xlsx' | 'pdf') {
    const r = await this.client.get(`/checador/asistencia/historial.${formato}`, { params: f, responseType: 'blob' });
    const tipo = formato === 'pdf'
      ? 'application/pdf'
      : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    const url = window.URL.createObjectURL(new Blob([r.data], { type: tipo }));
    const a = document.createElement('a');
    a.href = url; a.download = `Registro_asistencia.${formato}`; a.click();
    window.URL.revokeObjectURL(url);
  }
}

export const api = new APIClient();
export default api;
